import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import TableSortLabel from "@mui/material/TableSortLabel";
import Drawer from "@mui/material/Drawer";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import apiService, { viewGcpDocument } from "../../services/actions";
import TRFpopup from "./TRFpopup";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import OrderClinicalAndSampleTrack from "./OrderClinicalAndSampleTrack";
import DicomViewerDialog from "./DicomViewerDialog";
import OrderLabTestsSection from "./OrderLabTestsSection";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

dayjs.extend(utc);

const ORDER_TRACKING_DETAILS = [
  { sampleStatusId: 19, sampleStatus: "Order Placed" },
  { sampleStatusId: 1, sampleStatus: "Sample Drawn" },
  { sampleStatusId: 8, sampleStatus: "Test Conducted" },
  { sampleStatusId: 7, sampleStatus: "Processing Time" },
  { sampleStatusId: 12, sampleStatus: "Received at Clinic" },
  { sampleStatusId: 14, sampleStatus: "Bar Code Mapped" },
  { sampleStatusId: 15, sampleStatus: "Centrifuged" },
  { sampleStatusId: 2, sampleStatus: "Ready to ship" },
  { sampleStatusId: 3, sampleStatus: "Shipped from Clinic" },
  { sampleStatusId: 5, sampleStatus: "Reached Lab" },
  { sampleStatusId: 21, sampleStatus: "Report Generated" },
  { sampleStatusId: 22, sampleStatus: "Report Uploaded" },
  { sampleStatusId: 23, sampleStatus: "Consultation Consent" },
  { sampleStatusId: 24, sampleStatus: "Doctor Consultation" },
  { sampleStatusId: 25, sampleStatus: "Prescription Fulfillment" },
];

const STATUS_COLOR_MAP = {
  COMPLETED: "#16a34a",
  PARTIAL: "#f59e0b",
  PENDING: "#3b82f6",
  CANCELLED: "#ef4444",
  DEFAULT: "#6b7280",
};

const ORDER_TYPE_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Collection Center", label: "B2B (Collection Centers)" },
  { value: "b2b2C", label: "B2B2C" },
  { value: "Lab", label: "B2C (eClinics)" },
  { value: "d2c", label: "D2C" },
];

const DATE_RANGE_OPTIONS = [
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "custom", label: "Custom Range" },
];

const TEST_TYPE_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Pathology", label: "Pathology" },
  { value: "Radiology", label: "Radiology" },
];

const ORDER_STATUS_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Partial Completed", label: "Partial Completed" },
  { value: "Completed", label: "Completed" },
  { value: "Pending", label: "Pending" },
  { value: "Cancelled", label: "Cancelled" },
];

const REPORT_STATUS_OPTIONS = [
  { value: "Partial Completed", label: "Partial Completed" },
  { value: "Completed", label: "Completed" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const getOrderStatusColor = (status) => {
  if (!status) return "default";
  const s = status.toLowerCase();
  if (s.includes("completed") || s.includes("complete")) return "success";
  if (s.includes("partial")) return "warning";
  if (s.includes("pending")) return "info";
  if (s.includes("cancelled") || s.includes("canceled")) return "error";
  return "default";
};

const getOrderStatusColorCode = (status) => {
  if (!status) return STATUS_COLOR_MAP.DEFAULT;
  const s = status.toLowerCase();
  if (s.includes("completed") || s.includes("complete")) return STATUS_COLOR_MAP.COMPLETED;
  if (s.includes("partial")) return STATUS_COLOR_MAP.PARTIAL;
  if (s.includes("pending")) return STATUS_COLOR_MAP.PENDING;
  if (s.includes("cancelled") || s.includes("canceled")) return STATUS_COLOR_MAP.CANCELLED;
  return STATUS_COLOR_MAP.DEFAULT;
};

export const isOrderCancelled = (status) => {
  if (!status) return false;
  const s = String(status).trim().toLowerCase();
  return s.includes("cancelled") || s.includes("canceled");
};

export const isOrderCancelledOrRouted = (status) => {
  if (!status) return false;
  const s = String(status).trim().toLowerCase();
  return s.includes("cancelled") || s.includes("canceled") || s.includes("routed");
};


const getStatusText = (status, procedureStatus) => {
  if (status === 3) return "Partial";
  if (status === 2) return "Complete";
  if (status === 1) return "Pending";
  if (status === 0) return "Cancelled";
  if (procedureStatus === 4) return "Cancelled";
  return "Pending";
};

const formatDateToMMDDYYYY = (date) => (date ? dayjs(date).format("MM/DD/YYYY") : "");
const formatDateToDDMMYYYYHHMM = (date) => (date ? dayjs(date).format("DD/MM/YYYY HH:mm") : "Not available");

const formatUtcToLocalDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    let s = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const parsed = dayjs(s);
      return parsed.isValid() ? parsed.format("DD/MM/YYYY") : s;
    }
    // The API response time is already in local time; strip trailing Z if present to prevent shifting
    const cleanStr = s.replace(/Z$/, "");
    const parsed = dayjs(cleanStr);
    if (parsed.isValid()) {
      return parsed.format("DD/MM/YYYY, hh:mm:ss A");
    }
    return String(dateStr);
  } catch {
    return String(dateStr);
  }
};

const getGenderDisplay = (gender) => {
  if (gender === "F") return "Female";
  if (gender === "M") return "Male";
  return "—";
};

const getPatientDisplay = (details) => {
  const name = details?.patientName || "";
  const gender = getGenderDisplay(details?.gender);
  const id = details?.patientId ? `(${details.patientId})` : "";
  return `${name} / ${gender} / ${id}`;
};

const getLabConfirmIdStatusDisplay = (details) => {
  if (!details?.extLabConfirmIdStatus && !details?.extLabConfirmId) return "-";
  return details?.extLabConfirmIdStatus === "M" ? "Manual" : "Auto";
};

const getReportStatusDisplay = (rec) => rec.reportStatus || "";

const getUploadButtonColor = (order) => {
  if (order?.b2b2cType === "Y") return "third";
  return order?.reportName ? "secondary" : "primary";
};

const getUploadTitle = (order) => {
  if (order?.b2b2cType === "Y") return "Not Applicable";
  if (!order?.labConfirmId) return "Please add Booking ID first";
  return "Upload Report";
};

const normalizeToDayjs = (date) => (dayjs.isDayjs(date) ? date : dayjs(date));

const encodeBase64Files = async (files) =>
  Promise.all(
    files.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = () => reject(new Error("File reading failed"));
        })
    )
  );

const transformOrdersData = (ordersData) =>
  (ordersData || []).map((order) => ({
    id: order.orderId,
    orderId: order.orderId,
    eclinicName: order.eclinicName,
    assignedEclinicName: order.assignedEclinicName,
    patientName: order.patientName,
    ageGender: `${order.age}/${order.gender === "M" ? "Male" : "Female"}`,
    labCode: order.hospitalId || "Add ID",
    labConfirmId: order.extLabConfirmId || "",
    status: getStatusText(order.status, order.procedureStatus),
    testName: order.testName || "Lab Test",
    date: formatDateToDDMMYYYYHHMM(order.paymentDateTime) || "Not available",
    reportName: order.reportName || null,
    reportStatus: order.status || 1,
    testType: order.testType || "Pathology",
    originalData: order,
    orderStatus: order.orderStatus,
    reportSend: order.reportSend,
    b2b2cType: order.b2b2cType,
    smartReportStatus: order?.smartReportStatus || "Pending",
    sampleStatus: order.sampleStatus,
    patientCode: order.patientCode || order.patientId || order.patientid || "",
    hasReport: !!order.reportName,
  }));

const buildUploadPayload = ({ files, base64Strings, userCode, currentDateTime, selectedLabReport, selectedOrder, reportStatus }) => {
  if (files.length === 0) {
    return [
      {
        createdBy: userCode,
        createdDate: currentDateTime,
        labOrderDoc: null,
        labTestCode: null,
        labTestName: null,
        modifiedBy: userCode,
        modifiedDate: currentDateTime,
        orderId: selectedOrder?.id,
        status: 1,
        type: null,
        reportStatus,
      },
    ];
  }
  return files.map((file, index) => {
    const fileType = file?.type?.split("/")?.[1];
    let normalizedType;
    if (fileType === "vnd.openxmlformats-officedocument.spreadsheetml.sheet") normalizedType = "xlsx";
    else if (fileType === "vnd.ms-excel") normalizedType = "xls";
    else normalizedType = fileType;
    return {
      createdBy: userCode,
      createdDate: currentDateTime,
      labOrderDoc: base64Strings[index],
      labTestCode: selectedLabReport?.labTestCode,
      labTestName: selectedLabReport?.labTestName,
      modifiedBy: userCode,
      modifiedDate: currentDateTime,
      orderId: selectedOrder?.id,
      status: 1,
      type: normalizedType,
      reportStatus,
    };
  });
};

const validateUpload = (files, status) => {
  if (!status) return "Please select report status";
  if (files.length === 0 && (status === "Partial Completed" || status === "Completed")) return "Please select file";
  return null;
};

const buildEclinicLabPayload = (filters, payload) => {
  if (filters.selectedEclinics?.length > 0 && !filters.selectedEclinics.some((e) => e?.eclinicCode === "All")) {
    payload.CHEId = filters.selectedEclinics.map((e) => e.eclinicCode).join(",");
  }
  if (filters.selectedLabs?.length > 0 && !filters.selectedLabs.some((l) => l?.labCode === "All")) {
    payload.hospitalId = filters.selectedLabs.map((l) => l.labCode).join(",");
  }
};

const createAllPayload = (filters, payload) => {
  buildEclinicLabPayload(filters, payload);
  if (filters.selectedCcs?.length > 0 && !filters.selectedCcs.some((cc) => cc?.labCode === "All")) {
    payload.ccHospitalId = filters.selectedCcs.map((cc) => cc.labCode).join(",");
  }
};

const createLabPayload = (filters, payload) => buildEclinicLabPayload(filters, payload);
const createCollectionCenterPayload = (filters, payload) => createAllPayload(filters, payload);
const b2b2C = (filters, payload) => createAllPayload(filters, payload);
const createD2cPayload = (filters, payload) => {
  if (filters.selectedLabs?.length > 0 && !filters.selectedLabs.some((l) => l?.labCode === "All")) {
    payload.hospitalId = filters.selectedLabs.map((l) => l.labCode).join(",");
  }
};

const applyOrderTypePayload = (diagnosticType, filters, payload) => {
  const payloadBuilders = {
    All: createAllPayload,
    Lab: createLabPayload,
    "Collection Center": createCollectionCenterPayload,
    d2c: createD2cPayload,
    b2b2C,
  };
  if (diagnosticType === "All") { createAllPayload(filters, payload); return; }
  new Set(diagnosticType.split(",")).forEach((type) => {
    const build = payloadBuilders[type];
    if (build) build(filters, payload);
  });
};

const buildOrderStatusParam = (orderStatus) => {
  if (!orderStatus || orderStatus.length === 0) return undefined;
  const filtered = orderStatus.filter((s) => s !== "All");
  return filtered.length > 0 ? filtered.join(",") : undefined;
};

const buildDiagnosticType = (orderTypeArr) => {
  if (orderTypeArr.length === 0 || orderTypeArr.includes("All")) return "All";
  return orderTypeArr.join(",");
};

const buildApiPayload = (filters, pageNo, orderTypeArr, pageSz, getDateRangeValuesFn) => {
  const dateRangeValues = getDateRangeValuesFn();
  const payload = {
    orderId: filters?.orderId || undefined,
    patientName: filters?.patientName || undefined,
    fromDate: filters?.fromDate ? formatDateToMMDDYYYY(filters.fromDate) : formatDateToMMDDYYYY(dateRangeValues.fromDate),
    toDate: filters?.toDate ? formatDateToMMDDYYYY(filters.toDate) : formatDateToMMDDYYYY(dateRangeValues.toDate),
    testType: filters.testType === "All" ? undefined : filters.testType || undefined,
    mobileNo: filters.patientMobile || undefined,
    pageNumber: pageNo,
    pageSize: pageSz,
    hospitalId: filters?.hospitalId,
  };
  const orderStatusParam = buildOrderStatusParam(filters.orderStatus);
  if (orderStatusParam) payload.orderStatus = orderStatusParam;
  const diagnosticType = buildDiagnosticType(orderTypeArr);
  payload.diagnosticType = diagnosticType || "All";
  applyOrderTypePayload(diagnosticType, filters, payload);
  Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);
  return payload;
};

const mapQueryParamsToFilters = (queryParams, initialSearchFilters, setOrderType) => {
  const updated = { ...initialSearchFilters };
  ["orderId", "patientMobile", "patientName", "testType", "hospitalId"].forEach((key) => {
    if (queryParams[key]) updated[key] = queryParams[key];
  });
  if (queryParams.orderStatus) updated.orderStatus = queryParams.orderStatus.split(",");
  if (queryParams.orderType) {
    const typesSet = new Set(queryParams.orderType.split(","));
    setOrderType(ORDER_TYPE_OPTIONS.filter((opt) => typesSet.has(opt.value)));
  }
  if (queryParams.fromDate) updated.fromDate = dayjs(queryParams.fromDate, "MM/DD/YYYY");
  if (queryParams.toDate) updated.toDate = dayjs(queryParams.toDate, "MM/DD/YYYY");
  return updated;
};

const createPaginationButton = (pageNumber, isCurrent, fetchOrdersWithParams, pageSize) => (
  <Button
    key={pageNumber}
    variant={isCurrent ? "contained" : "outlined"}
    onClick={() => fetchOrdersWithParams(pageNumber)}
    sx={{
      minWidth: "32px", width: "32px", height: "32px", borderRadius: "4px", fontSize: "12px",
      fontWeight: isCurrent ? 600 : 400,
      backgroundColor: isCurrent ? "#004c97" : "white",
      border: isCurrent ? "none" : "1px solid #d1d5db",
      color: isCurrent ? "white" : "#374151",
      "&:hover": { backgroundColor: isCurrent ? "#003366" : "#f3f4f6" },
    }}
  >
    {pageNumber}
  </Button>
);

const PaginationEllipsis = ({ keyName }) => (
  <Typography key={keyName} sx={{ px: 1, color: "#6b7280" }}>...</Typography>
);
PaginationEllipsis.propTypes = { keyName: PropTypes.string.isRequired };

function useSnackbar() {
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnackbarMessage = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const closeSnackbar = useCallback(() => setSnackbar((prev) => ({ ...prev, open: false })), []);
  return { snackbar, showSnackbarMessage, closeSnackbar };
}

function useLabData(authToken, testType) {
  const [labList, setLabList] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [eclinicData, setEclinicData] = useState([]);
  const isMountedRef = useRef(true);

  useEffect(() => { return () => { isMountedRef.current = false; }; }, []);

  const getLabListData = useCallback(async (resetLabs, setSearchFilters) => {
    if (resetLabs) setSearchFilters((prev) => ({ ...prev, selectedLabs: [] }));
    if (!isMountedRef.current) return;
    try {
      const response = await apiService.getlabsByType({ type: testType || "All" });
      if (response?.status === 200 && isMountedRef.current) {
        setLabList([{ labCode: "All", labName: "All" }, ...(response.data || [])]);
      }
    } catch (error) {
      console.error("Error fetching lab list:", error);
      if (isMountedRef.current) setLabList([]);
    }
  }, [testType]);

  const getCcListData = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const response = await apiService.getCCList({ diagnosticTypes: "Collection Center" });
      if (response?.data?.successList && isMountedRef.current) {
        setCcList([{ labCode: "All", labName: "All" }, ...response.data.successList]);
      }
    } catch (error) {
      console.error("Error fetching CC list:", error);
      if (isMountedRef.current) setCcList([]);
    }
  }, []);

  const getEclinicsData = useCallback(async (searchTerm) => {
    if (!isMountedRef.current || !searchTerm || searchTerm.length < 3) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_COMMON_URL}EclinicAddress/list?eclinicName=${searchTerm}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (response?.data?.data && isMountedRef.current) setEclinicData(response.data.data || []);
    } catch (error) {
      console.error("Error fetching eClinics:", error);
      if (isMountedRef.current) setEclinicData([]);
    }
  }, [authToken]);

  return { labList, setLabList, ccList, eclinicData, setEclinicData, getLabListData, getCcListData, getEclinicsData };
}

function useReportData(showSnackbarMessage) {
  const [smartReports, setSmartReports] = useState([]);
  const [reportsLog, setReportsLog] = useState([]);
  const [labTestsOrderReports, setLabTestsOrderReports] = useState([]);
  const [pacsStudies, setPacsStudies] = useState([]);
  const [pacsLoading, setPacsLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => { return () => { isMountedRef.current = false; }; }, []);

  const getSmartReports = useCallback(async (orderId) => {
    if (!isMountedRef.current) return;
    try {
      const response = await apiService.getSmartReports({ orderId });
      if (isMountedRef.current) {
        setSmartReports(response?.data?.responseCode === "200" ? response.data.successObject : []);
      }
    } catch (error) {
      console.error("Error fetching smart reports:", error);
      if (isMountedRef.current) setSmartReports([]);
    }
  }, []);

  const getReportsLogs = useCallback(async (orderId) => {
    if (!isMountedRef.current) return;
    try {
      const response = await apiService.getReportsLogs({ orderId });
      if (isMountedRef.current) setReportsLog(response?.data || []);
    } catch (error) {
      console.error("Error fetching reports log:", error);
      if (isMountedRef.current) setReportsLog([]);
    }
  }, []);

  const fetchLabReports = useCallback(async (orderId) => {
    if (!orderId) return;
    try {
      const response = await apiService.getLabTestsOrderReport({ orderId, status: 1, docmentRequired: "Y" });
      if (response?.data.responseCode === "200" && response?.data?.successList) {
        setLabTestsOrderReports(response.data.successList);
      }
    } catch (error) {
      console.error("Error fetching lab tests order report:", error);
    }
  }, []);

  const getPacsStudies = useCallback(async (orderId) => {
    if (!orderId) {
      if (isMountedRef.current) setPacsStudies([]);
      return;
    }
    if (isMountedRef.current) setPacsLoading(true);
    try {
      const response = await apiService.getCrelioPacsDetails(orderId);
      const studies =
        response?.data?.data?.studies ||
        response?.data?.studies ||
        (Array.isArray(response?.data?.data) ? response.data.data : []);
      if (isMountedRef.current) {
        setPacsStudies(Array.isArray(studies) ? studies : []);
      }
    } catch (error) {
      console.error("Error fetching PACS studies:", error);
      if (isMountedRef.current) setPacsStudies([]);
    } finally {
      if (isMountedRef.current) setPacsLoading(false);
    }
  }, []);

  const handleOpenViewer = useCallback((url) => {
    if (!url) {
      showSnackbarMessage("Viewer URL not available", "error");
      return;
    }
    globalThis.open(url, "_blank");
  }, [showSnackbarMessage]);

  const handleDownloadPacsZip = useCallback((url, fileName = "dicom_files.zip") => {
    if (!url) {
      showSnackbarMessage("DICOM ZIP download URL not available", "error");
      return;
    }
    try {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to trigger download, opening in new window:", error);
      globalThis.open(url, "_blank");
    }
  }, [showSnackbarMessage]);

  const handleDownloadClick = useCallback(async (rec) => {
    if (!rec?.labOrderDoc) {
      showSnackbarMessage("Download is not available at the moment. Please try after sometime.", "error");
      return;
    }
    const fileExtension = rec.type?.toLowerCase();
    const supportedExtensions = ["pdf", "png", "jpg", "jpeg", "xlsx", "xls"];
    if (!supportedExtensions.includes(fileExtension)) {
      showSnackbarMessage(`Unsupported file format: ${fileExtension}`, "error");
      return;
    }
    try {
      const docUrl = await apiService.getDocumentUrl(rec.labOrderDoc);
      if (!docUrl) {
        throw new Error("Unable to retrieve file download URL.");
      }
      if (["xlsx", "xls"].includes(fileExtension)) { globalThis.open(docUrl, "_blank"); return; }
      const response = await fetch(docUrl);
      if (!response.ok) throw new Error("Failed to fetch the file");
      const blob = await response.blob();
      const url = globalThis.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = `${rec.orderId}- Report`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("File download failed:", error);
      showSnackbarMessage(error.message || "Failed to download the file. Please try again later.", "error");
    }
  }, [showSnackbarMessage]);

  const handleSmartReportDownload = useCallback(async (report) => {
    if (!report?.smartReportUrl) { showSnackbarMessage("Smart report URL not available", "error"); return; }
    try {
      const response = await fetch(report.smartReportUrl);
      if (!response.ok) throw new Error("Failed to fetch the file");
      const blob = await response.blob();
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Smart_Report_${report.tempOrderId || report.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Smart report download failed:", error);
      showSnackbarMessage("Failed to download smart report", "error");
    }
  }, [showSnackbarMessage]);

  const handleSmartReportView = useCallback((report) => {
    if (report?.smartReportUrl) globalThis.open(report.smartReportUrl, "_blank");
    else showSnackbarMessage("Smart report URL not available", "error");
  }, [showSnackbarMessage]);

  const handleViewClick = useCallback((rec) => {
    if (!rec?.labOrderDoc) {
      showSnackbarMessage("Document not available.", "error");
      return;
    }
    viewGcpDocument(rec.labOrderDoc, (err) => showSnackbarMessage(err, "error"));
  }, [showSnackbarMessage]);

  return {
    smartReports, setSmartReports, reportsLog, setReportsLog,
    labTestsOrderReports, setLabTestsOrderReports,
    pacsStudies, setPacsStudies, pacsLoading,
    getSmartReports, getReportsLogs, fetchLabReports, getPacsStudies,
    handleDownloadClick, handleSmartReportDownload, handleSmartReportView, handleViewClick,
    handleOpenViewer, handleDownloadPacsZip,
  };
}

const ReportDeliveredCell = ({ reportSend }) => (
  <Typography sx={{ color: reportSend === 1 ? "green" : "", fontWeight: 600 }}>
    {reportSend === 1 ? "Yes" : "No"}
  </Typography>
);
ReportDeliveredCell.propTypes = { reportSend: PropTypes.number };

const SelectAllCheckbox = ({ isAllSelected, isIndeterminate, onSelectAll, color = "primary" }) => (
  <Checkbox
    size="small"
    icon={icon}
    checkedIcon={checkedIcon}
    indeterminateIcon={<IndeterminateCheckBoxIcon fontSize="small" />}
    checked={isAllSelected}
    indeterminate={isIndeterminate}
    onChange={onSelectAll}
    color={color}
    sx={{ p: 0.25 }}
  />
);
SelectAllCheckbox.propTypes = {
  isAllSelected: PropTypes.bool.isRequired,
  isIndeterminate: PropTypes.bool.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  color: PropTypes.string,
};

const MultiSelectDropdown = ({ options, value, onChange, label, getOptionLabel, getOptionValue, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const allOption = options.find((opt) => getOptionValue(opt) === "All");
  const otherOptions = options.filter((opt) => getOptionValue(opt) !== "All");
  const hasAllSelected = value.some((v) => getOptionValue(v) === "All");
  const selectedOthers = hasAllSelected ? [] : value;

  const isSelected = (option) => {
    const val = getOptionValue(option);
    if (val === "All") return hasAllSelected;
    if (hasAllSelected) return true;
    return selectedOthers.some((v) => getOptionValue(v) === val);
  };

  const handleToggle = (option) => {
    const val = getOptionValue(option);
    if (val === "All") { onChange(hasAllSelected ? [] : [allOption]); return; }
    if (hasAllSelected) { onChange(otherOptions.filter((opt) => getOptionValue(opt) !== val)); return; }
    const exists = selectedOthers.some((v) => getOptionValue(v) === val);
    const updated = exists ? selectedOthers.filter((v) => getOptionValue(v) !== val) : [...selectedOthers, option];
    onChange(updated.length === otherOptions.length ? [allOption] : updated);
  };

  const handleDelete = (item, e) => {
    if (e) e.stopPropagation();
    const val = getOptionValue(item);
    if (val === "All") { onChange([]); return; }
    if (hasAllSelected) { onChange(otherOptions.filter((opt) => getOptionValue(opt) !== val)); return; }
    const updated = value.filter((v) => getOptionValue(v) !== val);
    onChange(updated);
  };

  return (
    <>
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel sx={{ fontSize: "0.8125rem", top: "-2px", "&.MuiInputLabel-shrink": { top: 0 }, "&.Mui-focused": { color: "#004c97" } }}>{label}</InputLabel>
        <Select
          open={false} value={value} displayEmpty label={label}
          onClick={handleClick} IconComponent={KeyboardArrowDownIcon}
          sx={{
            height: 34,
            borderRadius: "7px",
            bgcolor: "#ffffff",
            fontSize: "0.8125rem",
            "& .MuiSelect-select": {
              py: "4px !important",
              display: "flex",
              alignItems: "center",
            },
            "& fieldset": { borderColor: "#CBD5E1" },
            "&:hover fieldset": { borderColor: "#94A3B8" },
            "&.Mui-focused fieldset": { borderColor: "#004c97", borderWidth: "1.5px" },
          }}
          renderValue={(selected) => {
            if (!selected || selected.length === 0) return "";
            if (selected.some((v) => getOptionValue(v) === "All")) {
              return (
                <Chip
                  label="All"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.72rem",
                    bgcolor: "#EFF6FF",
                    color: "#004c97",
                    fontWeight: 600,
                  }}
                  onDelete={(e) => handleDelete(allOption || { value: "All", label: "All" }, e)}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              );
            }
            const first = selected[0];
            return (
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", overflow: "hidden" }}>
                <Chip
                  key={getOptionValue(first)}
                  label={getOptionLabel(first)}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.72rem",
                    maxWidth: selected.length > 1 ? 85 : 120,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      px: 0.6,
                    },
                  }}
                  onDelete={(e) => handleDelete(first, e)}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                {selected.length > 1 && (
                  <Chip
                    label={`+${selected.length - 1}`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.72rem",
                      bgcolor: "#F1F5F9",
                      color: "#475569",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            );
          }}
        />
      </FormControl>
      <Popover
        open={open} anchorEl={anchorEl} onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            width: Math.max(anchorEl?.offsetWidth || 260, 240),
            maxHeight: 320,
            mt: 0.5,
            borderRadius: "10px",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)",
            border: "1px solid #E2E8F0",
          }
        }}
      >
        <Box sx={{ p: 1 }}>
          {options.map((option) => (
            <MenuItem key={getOptionValue(option)} onClick={() => handleToggle(option)}>
              <Checkbox checked={isSelected(option)} size="small" sx={{ mr: 1 }} />
              <Typography variant="body2">{getOptionLabel(option)}</Typography>
            </MenuItem>
          ))}
        </Box>
      </Popover>
    </>
  );
};
MultiSelectDropdown.propTypes = {
  options: PropTypes.array.isRequired, value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired, label: PropTypes.string.isRequired,
  getOptionLabel: PropTypes.func.isRequired, getOptionValue: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const SearchableMultiSelectDropdown = ({ options, value, onChange, label, getOptionLabel, getOptionValue, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => { setAnchorEl(null); setSearchTerm(""); };

  const allOption = options.find((opt) => getOptionValue(opt) === "All");
  const otherOptions = options.filter((opt) => getOptionValue(opt) !== "All");
  const hasAllSelected = value.some((v) => getOptionValue(v) === "All");

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((opt) => getOptionLabel(opt).toLowerCase().includes(searchTerm.toLowerCase()));
  }, [options, searchTerm, getOptionLabel]);

  const isSelected = (option) => {
    const val = getOptionValue(option);
    if (val === "All") return hasAllSelected;
    if (hasAllSelected) return true;
    return value.some((v) => getOptionValue(v) === val);
  };

  const handleToggle = (option) => {
    const val = getOptionValue(option);
    if (val === "All") { onChange(hasAllSelected ? [] : [allOption]); return; }
    if (hasAllSelected) { onChange(otherOptions.filter((opt) => getOptionValue(opt) !== val)); return; }
    const exists = value.some((v) => getOptionValue(v) === val);
    const updated = exists ? value.filter((v) => getOptionValue(v) !== val) : [...value, option];
    onChange(updated.length === otherOptions.length ? [allOption] : updated);
  };

  const handleDelete = (item, e) => {
    if (e) e.stopPropagation();
    const val = getOptionValue(item);
    if (val === "All") { onChange([]); return; }
    if (hasAllSelected) { onChange(otherOptions.filter((opt) => getOptionValue(opt) !== val)); return; }
    const updated = value.filter((v) => getOptionValue(v) !== val);
    onChange(updated);
  };

  return (
    <>
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel sx={{ fontSize: "0.8125rem", top: "-2px", "&.MuiInputLabel-shrink": { top: 0 }, "&.Mui-focused": { color: "#004c97" } }}>{label}</InputLabel>
        <Select
          open={false} value={value} displayEmpty label={label}
          onClick={handleClick} IconComponent={KeyboardArrowDownIcon}
          sx={{
            height: 34,
            borderRadius: "7px",
            bgcolor: "#ffffff",
            fontSize: "0.8125rem",
            "& .MuiSelect-select": {
              py: "4px !important",
              display: "flex",
              alignItems: "center",
            },
            "& fieldset": { borderColor: "#CBD5E1" },
            "&:hover fieldset": { borderColor: "#94A3B8" },
            "&.Mui-focused fieldset": { borderColor: "#004c97", borderWidth: "1.5px" },
          }}
          renderValue={(selected) => {
            if (!selected || selected.length === 0) return "";
            if (selected.some((v) => getOptionValue(v) === "All")) {
              return (
                <Chip
                  label="All"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.72rem",
                    bgcolor: "#EFF6FF",
                    color: "#004c97",
                    fontWeight: 600,
                  }}
                  onDelete={(e) => handleDelete(allOption || { labCode: "All", labName: "All" }, e)}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              );
            }
            const first = selected[0];
            return (
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", overflow: "hidden" }}>
                <Chip
                  key={getOptionValue(first)}
                  label={getOptionLabel(first)}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.72rem",
                    maxWidth: selected.length > 1 ? 85 : 120,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      px: 0.6,
                    },
                  }}
                  onDelete={(e) => handleDelete(first, e)}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                {selected.length > 1 && (
                  <Chip
                    label={`+${selected.length - 1}`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.72rem",
                      bgcolor: "#F1F5F9",
                      color: "#475569",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            );
          }}
        />
      </FormControl>
      <Popover
        open={open} anchorEl={anchorEl} onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            width: Math.max(anchorEl?.offsetWidth || 260, 240),
            maxHeight: 320,
            mt: 0.5,
            borderRadius: "10px",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)",
            border: "1px solid #E2E8F0",
          }
        }}
      >
        <Box sx={{ p: 1.5, width: Math.max(anchorEl?.offsetWidth || 280, 260) }}>
          <TextField
            fullWidth size="small" placeholder="Search..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 1 }}
          />
          <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
            {filteredOptions.map((option) => (
              <MenuItem key={getOptionValue(option)} onClick={() => handleToggle(option)} sx={{ fontSize: "0.85rem", py: 0.5 }}>
                <Checkbox checked={isSelected(option)} size="small" sx={{ mr: 1 }} />
                {getOptionLabel(option)}
              </MenuItem>
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
};
SearchableMultiSelectDropdown.propTypes = {
  options: PropTypes.array.isRequired, value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired, label: PropTypes.string.isRequired,
  getOptionLabel: PropTypes.func.isRequired, getOptionValue: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const TRACKING_LABEL_MAP = {
  "Sample Collected": "Sample Drawn",
  "Sample ready to dispatch": "Ready to Ship",
  Picked: "Shipped from Clinic",
  Delivered: "Reached Lab",
  "Centrifuge Mapped": "Centrifuged",
};

const getStatusLabel = (step) => {
  if (!step?.sampleStatus) return "Unknown";
  const baseLabel = step.sampleStatus.charAt(0).toUpperCase() + step.sampleStatus.slice(1);
  return TRACKING_LABEL_MAP[baseLabel] || baseLabel;
};

const OrderTrackingDetails = React.memo(({ orderTrackDetails = [], testType = "pathology" }) => {
  const filterSteps = useMemo(() => {
    return orderTrackDetails.filter((step) => {
      if (!step?.sampleStatusId) return false;
      const testTypeLower = testType?.toLowerCase();
      if (testTypeLower === "radiology") return [19, 22, 23, 24, 7].includes(step.sampleStatusId);
      if (testTypeLower === "pathology") return ![8, 7, 14].includes(step.sampleStatusId);
      return true;
    });
  }, [orderTrackDetails, testType]);

  if (!orderTrackDetails?.length) {
    return (
      <Box sx={{ backgroundColor: "white", p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No tracking information available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "white", p: 2, borderRadius: 1 }}>
      {filterSteps.map((item) => {
        const isCompleted = !!item.sampleStatusUpdated;
        const uniqueKey = `${item.sampleStatusId}_${item.sampleStatus}_${item.sampleStatusUpdated || "pending"}`;
        return (
          <Box key={uniqueKey} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{
                width: 16, height: 16, borderRadius: "4px", display: "flex",
                alignItems: "center", justifyContent: "center",
                border: isCompleted ? "none" : "2px solid #d1d5db",
                backgroundColor: isCompleted ? "#16a34a" : "white",
                color: isCompleted ? "white" : "transparent",
              }}>
                {isCompleted && <CheckIcon sx={{ fontSize: 14 }} />}
              </Box>
              <Typography variant="body2">{getStatusLabel(item)}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {isCompleted && item.sampleStatusUpdated
                ? new Date(item.sampleStatusUpdated).toLocaleString("en-GB", {
                  weekday: "long", day: "numeric", month: "short",
                  hour: "2-digit", minute: "2-digit", hour12: true,
                })
                : "Pending"}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
});
OrderTrackingDetails.propTypes = { orderTrackDetails: PropTypes.array, testType: PropTypes.string };
OrderTrackingDetails.displayName = "OrderTrackingDetails";

const FileUploadModal = React.memo(({
  open, onClose, selectedFiles, setSelectedFiles, reportStatus, setReportStatus,
  selectedLabReport, setSelectedLabReport, typedLabReport, setTypedLabReport,
  patientLabTestsOrderDetails, handleSave, loading,
}) => {
  const handleFileChange = (event) => {
    setSelectedFiles((prev) => [...prev, ...Array.from(event.target.files)]);
    event.target.value = "";
  };
  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => { const f = [...prev]; f.splice(index, 1); return f; });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Card sx={{ maxWidth: "100vh", width: "100vh", maxHeight: "80vh", overflowY: "auto" }}>
          <CardHeader
            title="Upload Report"
            action={<IconButton onClick={onClose}><CloseIcon /></IconButton>}
            sx={{ backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}
          />
          <Divider />
          <CardContent>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Autocomplete
                  sx={{ width: "30rem" }}
                  options={patientLabTestsOrderDetails}
                  getOptionLabel={(option) => (typeof option === "string" ? option : option.labTestName)}
                  value={selectedLabReport}
                  onChange={(_, newValue) => setSelectedLabReport(newValue)}
                  inputValue={typedLabReport}
                  onInputChange={(_, newInputValue) => setTypedLabReport(newInputValue)}
                  freeSolo
                  renderInput={(params) => <TextField {...params} label="Select or type Lab Report" variant="outlined" size="small" />}
                />
                <FormControl sx={{ minWidth: 120 }} size="small">
                  <InputLabel>Report Status</InputLabel>
                  <Select value={reportStatus} label="Report Status" onChange={(e) => setReportStatus(e.target.value)}>
                    {REPORT_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="contained" component="label" startIcon={<CloudUploadIcon />}>
                  Choose Files
                  <input type="file" hidden multiple onChange={handleFileChange} />
                </Button>
              </Box>
              {selectedFiles.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Selected Files:</Typography>
                  {selectedFiles.map((file, index) => (
                    <Box key={`file-${index}-${file.name}-${file.size}`} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
                      <Typography variant="body2">{file.name}</Typography>
                      <IconButton size="small" onClick={() => handleRemoveFile(index)} color="error">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 3 }}>
                <Button variant="outlined" onClick={onClose}>Cancel</Button>
                <Button
                  variant="contained" onClick={handleSave}
                  disabled={loading || (selectedFiles.length === 0 && reportStatus === "Partial Completed")}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? "Uploading..." : "Save"}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Modal>
  );
});
FileUploadModal.propTypes = {
  open: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired,
  selectedFiles: PropTypes.array.isRequired, setSelectedFiles: PropTypes.func.isRequired,
  reportStatus: PropTypes.string, setReportStatus: PropTypes.func.isRequired,
  selectedLabReport: PropTypes.any, setSelectedLabReport: PropTypes.func.isRequired,
  typedLabReport: PropTypes.string.isRequired, setTypedLabReport: PropTypes.func.isRequired,
  patientLabTestsOrderDetails: PropTypes.array.isRequired,
  handleSave: PropTypes.func.isRequired, loading: PropTypes.bool.isRequired,
};
FileUploadModal.displayName = "FileUploadModal";

const LabConfirmIdCell = ({
  order,
  editingId,
  editValue,
  setEditValue,
  loading,
  onSave,
  onEdit,
  onCancel,
}) => {
  const isCancelled = isOrderCancelled(order.orderStatus);
  const isDisabled = loading || isCancelled;

  if (editingId === order.id && !isCancelled) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <TextField
          value={editValue}
          onChange={(e) =>
            setEditValue(e.target.value)
          }
          size="small"
          sx={{ width: 120 }}
          disabled={isDisabled}
        />
        <IconButton
          size="small"
          color="primary"
          onClick={() => onSave(order)}
          disabled={isDisabled}
        >
          <CheckIcon />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={onCancel}
          disabled={isDisabled}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography
        sx={{
          minWidth: 80,
          cursor: isDisabled ? "not-allowed" : "pointer",
          color: order.labConfirmId ? "inherit" : "text.secondary",
          opacity: isDisabled ? 0.5 : 1
        }}
        onClick={() => {
          if (isDisabled) return;
          onEdit(order);
        }}
        title={isCancelled ? "Editing Booking ID not allowed for Cancelled orders" : undefined}
      >
        {order.labConfirmId || "Add ID"}
      </Typography>
      {!isCancelled && (
        <IconButton
          size="small"
          onClick={() => onEdit(order)}
          disabled={isDisabled}
          title="Edit Booking ID"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
LabConfirmIdCell.propTypes = {
  order: PropTypes.object.isRequired, editingId: PropTypes.any,
  editValue: PropTypes.string.isRequired, setEditValue: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired, onSave: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired, onCancel: PropTypes.func.isRequired,
};

const OrderStatusCell = ({ order, loading, onStatusClick }) => {
  const statusStr = (order.orderStatus || "").toLowerCase();
  const isCancelled = isOrderCancelledOrRouted(order.orderStatus);
  const isCancellable =
    !isCancelled &&
    statusStr !== "completed" &&
    statusStr !== "complete";

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Chip
        label={order.orderStatus || "—"}
        color={getOrderStatusColor(order.orderStatus)}
        size="small" variant="filled"
        sx={{
          fontWeight: 600, minWidth: "100px",
          backgroundColor: getOrderStatusColorCode(order.orderStatus),
          color: "white", "& .MuiChip-label": { color: "white" },
        }}
      />
      {isCancellable && (
        <IconButton
          size="small" color="error"
          onClick={(e) => onStatusClick(e, order)}
          disabled={loading} title="Cancel Order"
          sx={{ ml: 0.5, "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.1)" } }}
        >
          <CancelIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
OrderStatusCell.propTypes = {
  order: PropTypes.object.isRequired, loading: PropTypes.bool.isRequired,
  onStatusClick: PropTypes.func.isRequired,
};

const SampleStatusCell = ({ order }) => {
  const status = ORDER_TRACKING_DETAILS.find((item) => item.sampleStatusId === order?.originalData?.sampleStatus);
  return <Chip label={status ? status.sampleStatus : "Pending"} variant="outlined" size="small" />;
};
SampleStatusCell.propTypes = { order: PropTypes.object.isRequired };

const TABLE_COLUMNS = [
  { key: "orderId", label: "Order ID", sortable: true },
  { key: null, label: "Billing Date", sortable: false },
  { key: null, label: "eClinic", sortable: false },
  { key: "patientName", label: "Patient", sortable: true },
  { key: null, label: "Age & Gender", sortable: false },
  { key: null, label: "Lab Confirm ID", sortable: false },
  { key: null, label: "Report", sortable: false },
  { key: null, label: "Report Delivered", sortable: false },
  { key: "orderStatus", label: "Order Status", sortable: true },
  { key: "labTestName", label: "Test Name", sortable: true },
  { key: null, label: "Sample Status", sortable: false },
];

const OrdersTable = ({
  orders, sort, onSort, selectedOrderIds, isAllSelected, isIndeterminate,
  onSelectAll, onSelectOrder, onOrderIdClick, editingId, editValue, setEditValue,
  loading, onSaveBookingId, onEditClick, onCancelEdit, onUploadClick, onStatusClick,
  bulkDownloadLoading, onBulkDownload,
}) => (
  <TableContainer
    component={Paper}
    sx={{
      flex: 1,
      minHeight: { xs: 350, md: 0 },
      height: "100%",
      maxHeight: { xs: "calc(100vh - 260px)", md: "100%" },
      overflow: "auto",
      borderRadius: "10px",
      border: "1px solid #E2E8F0",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
    }}
  >
    <Table stickyHeader size="small">
      <TableHead sx={{ bgcolor: "#f4f6f9" }}>
        <TableRow sx={{ height: 38 }}>
          <TableCell
            padding="none"
            sx={{
              width: "1%",
              whiteSpace: "nowrap",
              pl: 1,
              pr: 0.5,
              py: 0.75,
              bgcolor: "#f4f6f9 !important",
              position: "sticky",
              top: 0,
              zIndex: 10,
              borderBottom: "2px solid #CBD5E1",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
              <SelectAllCheckbox isAllSelected={isAllSelected} isIndeterminate={isIndeterminate} onSelectAll={onSelectAll} />
              <Tooltip title={selectedOrderIds.length > 0 ? `Download ${selectedOrderIds.length} Selected Report(s)` : "Select orders to download reports"}>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onBulkDownload}
                    disabled={selectedOrderIds.length === 0 || bulkDownloadLoading}
                    sx={{
                      minWidth: 0,
                      px: 0.6,
                      py: 0.2,
                      height: 22,
                      fontSize: "11px",
                      fontWeight: 600,
                      lineHeight: 1,
                      textTransform: "none",
                      borderRadius: "4px",
                      bgcolor: selectedOrderIds.length > 0 ? "#004c97" : "#CBD5E1",
                      color: selectedOrderIds.length > 0 ? "#ffffff" : "#64748B",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: selectedOrderIds.length > 0 ? "#002f5f" : "#CBD5E1",
                        boxShadow: "none",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "#E2E8F0",
                        color: "#94A3B8",
                      },
                    }}
                  >
                    {bulkDownloadLoading ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                        <DownloadOutlinedIcon sx={{ fontSize: 14 }} />
                        <span>({selectedOrderIds.length})</span>
                      </Box>
                    )}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </TableCell>
          {TABLE_COLUMNS.map(({ key, label, sortable }) =>
            sortable ? (
              <TableCell
                key={label}
                sx={{
                  bgcolor: "#f4f6f9 !important",
                  fontWeight: "bold",
                  fontSize: "13px",
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  zIndex: 9,
                  borderBottom: "2px solid #CBD5E1",
                  py: 0.75,
                  ...(key === "orderId" ? { pl: 0.75, pr: 1.5 } : { px: 1.5 }),
                }}
              >
                <TableSortLabel active={sort.sortBy === key} direction={sort.sortOrder} onClick={() => onSort(key)}>
                  {label}
                </TableSortLabel>
              </TableCell>
            ) : (
              <TableCell
                key={label}
                sx={{
                  bgcolor: "#f4f6f9 !important",
                  fontWeight: "bold",
                  fontSize: "13px",
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  zIndex: 9,
                  borderBottom: "2px solid #CBD5E1",
                  py: 0.75,
                  px: 1.5,
                }}
              >
                {label}
              </TableCell>
            )
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {orders.map((order) => (
          <TableRow key={`order-${order.id}`} hover selected={selectedOrderIds.includes(order.id)}>
            <TableCell
              padding="none"
              sx={{
                width: "1%",
                whiteSpace: "nowrap",
                pl: 1,
                pr: 0.5,
                py: 0.5,
              }}
            >
              <Checkbox
                size="small"
                checked={selectedOrderIds.includes(order.id)}
                onChange={() => onSelectOrder(order.id)}
                icon={icon} checkedIcon={checkedIcon}
                disabled={!order.hasReport}
                sx={{ p: 0.25 }}
              />
            </TableCell>
            <TableCell sx={{ pl: 0.75, pr: 1.5, py: 0.5, whiteSpace: "nowrap" }}>
              <Typography color="primary" sx={{ cursor: "pointer", textDecoration: "underline", fontSize: "13px", fontWeight: 500 }} onClick={() => onOrderIdClick(order)}>
                {order.id}
              </Typography>
            </TableCell>
            <TableCell>{order.date}</TableCell>
            <TableCell><Typography sx={{ cursor: "pointer" }}>{order.eclinicName}</Typography></TableCell>
            <TableCell><Typography sx={{ cursor: "pointer" }}>{order.patientName}</Typography></TableCell>
            <TableCell>{order.ageGender}</TableCell>
            <TableCell>
              <LabConfirmIdCell
                order={order} editingId={editingId} editValue={editValue} setEditValue={setEditValue}
                loading={loading} onSave={onSaveBookingId} onEdit={onEditClick} onCancel={onCancelEdit}
              />
            </TableCell>
            <TableCell>
              {(() => {
                const isCancelled = isOrderCancelled(order.orderStatus);
                const isUploadDisabled =
                  loading ||
                  isCancelled ||
                  (!order?.labConfirmId && order?.b2b2cType !== "Y");
                return (
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (isCancelled) return;
                      onUploadClick(order);
                    }}
                    disabled={isUploadDisabled}
                    color={getUploadButtonColor(order)}
                    title={
                      isCancelled
                        ? "Upload report not allowed for Cancelled orders"
                        : getUploadTitle(order)
                    }
                  >
                    <CloudUploadIcon fontSize="small" />
                  </IconButton>
                );
              })()}
            </TableCell>
            <TableCell><ReportDeliveredCell reportSend={order.reportSend} /></TableCell>
            <TableCell><OrderStatusCell order={order} loading={loading} onStatusClick={onStatusClick} /></TableCell>
            <TableCell>{order.testName}</TableCell>
            <TableCell><SampleStatusCell order={order} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);
OrdersTable.propTypes = {
  orders: PropTypes.array.isRequired, sort: PropTypes.object.isRequired, onSort: PropTypes.func.isRequired,
  selectedOrderIds: PropTypes.array.isRequired, isAllSelected: PropTypes.bool.isRequired,
  isIndeterminate: PropTypes.bool.isRequired, onSelectAll: PropTypes.func.isRequired,
  onSelectOrder: PropTypes.func.isRequired, onOrderIdClick: PropTypes.func.isRequired,
  editingId: PropTypes.any, editValue: PropTypes.string.isRequired, setEditValue: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired, onSaveBookingId: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired, onCancelEdit: PropTypes.func.isRequired,
  onUploadClick: PropTypes.func.isRequired, onStatusClick: PropTypes.func.isRequired,
  bulkDownloadLoading: PropTypes.bool.isRequired, onBulkDownload: PropTypes.func.isRequired,
};

const FilterSection = ({
  orderType, onOrderTypeChange, searchFilters, setSearchFilters,
  eclinicData, setEclinicData, getEclinicsData, labList, ccList,
  shouldShowEclinicFilter, shouldShowLabFilter, shouldShowCcFilter,
  onOrderStatusChange, onSearch, onReset, loading = false,
}) => {
  return (
  <Paper
    elevation={0}
    sx={{
      bgcolor: "#ffffff",
      borderRadius: "10px",
      border: "1px solid #E2E8F0",
      p: { xs: 1, sm: 1.25 },
      mb: 0.5,
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.02)",
    }}
  >
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(5, 1fr)",
          xl: "repeat(5, 1fr)",
        },
        rowGap: 0.85,
        columnGap: 1,
        alignItems: "center",
      }}
    >
      <Box>
        <MultiSelectDropdown
          options={ORDER_TYPE_OPTIONS} value={orderType} onChange={onOrderTypeChange}
          label="Order Type" getOptionLabel={(opt) => opt.label} getOptionValue={(opt) => opt.value}
        />
      </Box>

      <Box>
        <TextField
          fullWidth size="small" label="Order ID" placeholder="Search Order ID" value={searchFilters.orderId}
          onChange={(e) => setSearchFilters((p) => ({ ...p, orderId: e.target.value }))}
          onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 34, borderRadius: "7px", bgcolor: "#ffffff", fontSize: "0.8125rem",
              "& fieldset": { borderColor: "#CBD5E1" },
              "&:hover fieldset": { borderColor: "#94A3B8" },
              "&.Mui-focused fieldset": { borderColor: "#004c97", borderWidth: "1.5px" },
            },
            "& .MuiInputLabel-root": { fontSize: "0.8125rem", top: "-2px", "&.MuiInputLabel-shrink": { top: 0 } },
            "& .MuiInputLabel-root.Mui-focused": { color: "#004c97" },
          }}
        />
      </Box>

      <Box>
        <TextField
          fullWidth size="small" label="Mobile Number" placeholder="Search Mobile" value={searchFilters.patientMobile}
          onChange={(e) => setSearchFilters((p) => ({ ...p, patientMobile: e.target.value }))}
          onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 34, borderRadius: "7px", bgcolor: "#ffffff", fontSize: "0.8125rem",
              "& fieldset": { borderColor: "#CBD5E1" },
              "&:hover fieldset": { borderColor: "#94A3B8" },
              "&.Mui-focused fieldset": { borderColor: "#004c97", borderWidth: "1.5px" },
            },
            "& .MuiInputLabel-root": { fontSize: "0.8125rem", top: "-2px", "&.MuiInputLabel-shrink": { top: 0 } },
            "& .MuiInputLabel-root.Mui-focused": { color: "#004c97" },
          }}
        />
      </Box>

      <Box>
        <TextField
          fullWidth size="small" label="Patient Name" placeholder="Search Patient Name" value={searchFilters.patientName}
          onChange={(e) => setSearchFilters((p) => ({ ...p, patientName: e.target.value }))}
          onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 34, borderRadius: "7px", bgcolor: "#ffffff", fontSize: "0.8125rem",
              "& fieldset": { borderColor: "#CBD5E1" },
              "&:hover fieldset": { borderColor: "#94A3B8" },
              "&.Mui-focused fieldset": { borderColor: "#004c97", borderWidth: "1.5px" },
            },
            "& .MuiInputLabel-root": { fontSize: "0.8125rem", top: "-2px", "&.MuiInputLabel-shrink": { top: 0 } },
            "& .MuiInputLabel-root.Mui-focused": { color: "#004c97" },
          }}
        />
      </Box>

      <Box>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ fontSize: "0.8125rem", top: "-2px", "&.MuiInputLabel-shrink": { top: 0 }, "&.Mui-focused": { color: "#004c97" } }}>Test Type</InputLabel>
          <Select
            value={searchFilters.testType} label="Test Type"
            onChange={(e) => setSearchFilters((p) => ({ ...p, testType: e.target.value }))}
            sx={{
              height: 34, borderRadius: "7px", bgcolor: "#ffffff", fontSize: "0.8125rem",
              "& .MuiSelect-select": { py: "4px !important", display: "flex", alignItems: "center" },
              "& fieldset": { borderColor: "#CBD5E1" },
              "&:hover fieldset": { borderColor: "#94A3B8" },
              "&.Mui-focused fieldset": { borderColor: "#004c97", borderWidth: "1.5px" },
            }}
          >
            {TEST_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.8125rem" }}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <MultiSelectDropdown
          options={ORDER_STATUS_OPTIONS}
          value={ORDER_STATUS_OPTIONS.filter((opt) => searchFilters.orderStatus.includes(opt.value))}
          onChange={onOrderStatusChange} label="Order Status"
          getOptionLabel={(opt) => opt.label} getOptionValue={(opt) => opt.value}
        />
      </Box>

      {shouldShowEclinicFilter && (
        <Box>
          <Autocomplete
            multiple
            size="small"
            options={eclinicData || []}
            getOptionLabel={(o) => o?.eclinicName || ""}
            isOptionEqualToValue={(option, val) =>
              (option?.eclinicCode && val?.eclinicCode ? option.eclinicCode === val.eclinicCode : option?.eclinicName === val?.eclinicName)
            }
            value={searchFilters.selectedEclinics}
            onChange={(_, v) => setSearchFilters((p) => ({ ...p, selectedEclinics: v }))}
            onInputChange={(_, value) => {
              setEclinicData([]);
              if (value.length > 2) getEclinicsData(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="eClinics"
                size="small"
                placeholder="Select eClinics"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 34, borderRadius: "7px", bgcolor: "#ffffff", fontSize: "0.8125rem", py: "2px !important",
                    "& fieldset": { borderColor: "#CBD5E1" },
                    "&:hover fieldset": { borderColor: "#94A3B8" },
                    "&.Mui-focused fieldset": { borderColor: "#004c97", borderWidth: "1.5px" },
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.8125rem", top: "-2px", "&.MuiInputLabel-shrink": { top: 0 } },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#004c97" },
                }}
              />
            )}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ fontSize: "0.8125rem" }}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  size="small"
                />
                {option.eclinicName}
              </li>
            )}
            sx={{
              "& .MuiAutocomplete-tag": {
                maxWidth: "100%",
                fontSize: "0.75rem",
                m: "2px",
              },
            }}
          />
        </Box>
      )}

      {shouldShowLabFilter && (
        <Box>
          <SearchableMultiSelectDropdown
            label="Labs" options={labList} value={searchFilters.selectedLabs}
            onChange={(val) => setSearchFilters((p) => ({ ...p, selectedLabs: val }))}
            getOptionLabel={(opt) => opt.labName} getOptionValue={(opt) => opt.labCode}
          />
        </Box>
      )}

      {shouldShowCcFilter && (
        <Box>
          <SearchableMultiSelectDropdown
            label="Collection Centers" options={ccList} value={searchFilters.selectedCcs}
            onChange={(val) => setSearchFilters((p) => ({ ...p, selectedCcs: val }))}
            getOptionLabel={(opt) => opt.labName} getOptionValue={(opt) => opt.labCode}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 0.75, height: 34, alignItems: "center" }}>
        <Button
          variant="contained" fullWidth onClick={onSearch} disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon sx={{ fontSize: 16 }} />}
          sx={{
            bgcolor: "#004c97", "&:hover": { bgcolor: "#00376d" },
            borderRadius: "7px", textTransform: "none", fontWeight: 600, fontSize: "0.8125rem",
            height: "100%", boxShadow: "none", flex: 1,
          }}
        >
          Search
        </Button>
        {onReset && (
          <Button
            variant="outlined" onClick={onReset}
            startIcon={<RefreshOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderColor: "#CBD5E1", color: "#475569",
              "&:hover": { bgcolor: "#F8FAFC", borderColor: "#94A3B8" },
              borderRadius: "7px", textTransform: "none", fontWeight: 600, fontSize: "0.8125rem",
              height: "100%", px: 1.25, minWidth: 75,
            }}
          >
            Reset
          </Button>
        )}
      </Box>
    </Box>
  </Paper>
  );
};
FilterSection.propTypes = {
  orderType: PropTypes.array.isRequired, onOrderTypeChange: PropTypes.func.isRequired,
  searchFilters: PropTypes.object.isRequired, setSearchFilters: PropTypes.func.isRequired,
  eclinicData: PropTypes.array.isRequired, setEclinicData: PropTypes.func.isRequired,
  getEclinicsData: PropTypes.func.isRequired, labList: PropTypes.array.isRequired,
  ccList: PropTypes.array.isRequired, shouldShowEclinicFilter: PropTypes.bool.isRequired,
  shouldShowLabFilter: PropTypes.bool.isRequired, shouldShowCcFilter: PropTypes.bool.isRequired,
  onOrderStatusChange: PropTypes.func.isRequired, onSearch: PropTypes.func.isRequired,
  onReset: PropTypes.func, loading: PropTypes.bool,
};

const PaginationSection = ({ currentPage, totalPages, totalElements, pageSize, onPageSizeChange, onPageChange, fetchOrdersWithParams }) => {
  const buttons = useMemo(() => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    if (start > 1) {
      pages.push(createPaginationButton(1, false, fetchOrdersWithParams, pageSize));
      if (start > 2) pages.push(<PaginationEllipsis keyName="ellipsis1" />);
    }
    for (let p = start; p <= end; p++) {
      pages.push(createPaginationButton(p, currentPage === p, fetchOrdersWithParams, pageSize));
    }
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push(<PaginationEllipsis keyName="ellipsis2" />);
      pages.push(createPaginationButton(totalPages, false, fetchOrdersWithParams, pageSize));
    }
    return pages;
  }, [currentPage, totalPages, pageSize, fetchOrdersWithParams]);

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.75}>
      <Typography variant="body2" color="text.secondary">
        Showing {Math.min((currentPage - 1) * pageSize + 1, totalElements)} to{" "}
        {Math.min(currentPage * pageSize, totalElements)} of {totalElements} entries
      </Typography>
      <Box display="flex" alignItems="center" gap={2}>
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select value={pageSize} onChange={onPageSizeChange} displayEmpty>
            {PAGE_SIZE_OPTIONS.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
          </Select>
        </FormControl>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Button
            variant="outlined" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
            sx={{
              minWidth: "80px", px: 2, py: 0.5, borderRadius: "4px", border: "1px solid #d1d5db",
              backgroundColor: "white", color: currentPage === 1 ? "#9ca3af" : "#374151",
              fontSize: "12px", fontWeight: 500, textTransform: "none",
              "&:disabled": { backgroundColor: "#f9fafb", color: "#9ca3af" },
              "&:hover": { backgroundColor: "#f3f4f6", borderColor: "#9ca3af" }
            }}
          >
            Previous
          </Button>
          {buttons}
          <Button
            variant="outlined" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages}
            sx={{
              minWidth: "80px", px: 2, py: 0.5, borderRadius: "4px", border: "1px solid #d1d5db",
              backgroundColor: "white", color: currentPage >= totalPages ? "#9ca3af" : "#374151",
              fontSize: "12px", fontWeight: 500, textTransform: "none",
              "&:disabled": { backgroundColor: "#f9fafb", color: "#9ca3af" },
              "&:hover": { backgroundColor: "#f3f4f6", borderColor: "#9ca3af" }
            }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
PaginationSection.propTypes = {
  currentPage: PropTypes.number.isRequired, totalPages: PropTypes.number.isRequired,
  totalElements: PropTypes.number.isRequired, pageSize: PropTypes.number.isRequired,
  onPageSizeChange: PropTypes.func.isRequired, onPageChange: PropTypes.func.isRequired,
  fetchOrdersWithParams: PropTypes.func.isRequired,
};

const SidebarLabConfirmId = ({
  selectedOrder,
  editingId,
  editValue,
  setEditValue,
  loading,
  onSave,
  onCancel,
  onStartEdit
}) => {
  if (!selectedOrder) return null;

  const isCancelled = isOrderCancelled(
    selectedOrder.orderStatus || selectedOrder.originalData?.orderStatus
  );
  const isDisabled = loading || isCancelled;

  if (editingId === selectedOrder.id && !isCancelled) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <TextField
          value={editValue}
          onChange={(e) =>
            setEditValue(e.target.value)
          }
          size="small"
          sx={{ width: 120 }}
          disabled={isDisabled}
        />
        <IconButton
          size="small"
          color="primary"
          onClick={() => onSave(selectedOrder)}
          disabled={isDisabled}
        >
          <CheckIcon />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={onCancel}
          disabled={isDisabled}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  const confirmId =
    selectedOrder.labConfirmId ||
    selectedOrder.originalData?.extLabConfirmId;

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography
        sx={{
          minWidth: 80,
          cursor: isDisabled ? "not-allowed" : "pointer",
          color: confirmId ? "inherit" : "text.secondary",
          opacity: isDisabled ? 0.5 : 1
        }}
        onClick={() => {
          if (isDisabled) return;
          onStartEdit(selectedOrder.id, confirmId || "");
        }}
        title={isCancelled ? "Editing Booking ID not allowed for Cancelled orders" : undefined}
      >
        {confirmId || "Add ID"}
      </Typography>
      {!isCancelled && (
        <IconButton
          size="small"
          onClick={() => onStartEdit(selectedOrder.id, confirmId || "")}
          disabled={isDisabled}
          title="Edit Booking ID"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
SidebarLabConfirmId.propTypes = {
  selectedOrder: PropTypes.object, editingId: PropTypes.any,
  editValue: PropTypes.string.isRequired, setEditValue: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired, onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired, onStartEdit: PropTypes.func.isRequired,
};

const ReportTable = ({ reports, onView, onDownload, onDelete }) => (
  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow>
          {["Lab Tests", "View", "Download", "Action", "Uploaded By", "Report Status"].map((h) => (
            <TableCell key={h} sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {reports.map((rec) => (
          <TableRow key={`report-${rec.id}-${rec.labTestCode}`}>
            <TableCell>{rec?.labTestName}</TableCell>
            <TableCell><IconButton onClick={() => onView(rec)}><VisibilityIcon /></IconButton></TableCell>
            <TableCell><IconButton onClick={() => onDownload(rec)}><DownloadOutlinedIcon /></IconButton></TableCell>
            <TableCell><IconButton onClick={() => onDelete(rec)}><DeleteIcon /></IconButton></TableCell>
            <TableCell>{rec.createdByName}</TableCell>
            <TableCell>{getReportStatusDisplay(rec)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);
ReportTable.propTypes = {
  reports: PropTypes.array.isRequired, onView: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired, onDelete: PropTypes.func.isRequired,
};

const CancelOrderDialog = ({ open, onClose, orderToCancel, loading, onConfirm }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>
      <Box display="flex" alignItems="center" gap={1}>
        <CancelIcon color="error" />
        <Typography variant="h6">Cancel Order</Typography>
      </Box>
    </DialogTitle>
    <DialogContent>
      <DialogContentText>
        Are you sure you want to cancel order <strong>{orderToCancel?.id}</strong> for patient{" "}
        <strong>{orderToCancel?.patientName}</strong>?
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>Cancel</Button>
      <Button onClick={() => onConfirm(orderToCancel)} color="error" variant="contained">
        {loading ? <CircularProgress size={24} /> : "Confirm Cancellation"}
      </Button>
    </DialogActions>
  </Dialog>
);
CancelOrderDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orderToCancel: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

const SmartReportsEmpty = () => (
  <Box sx={{ textAlign: "center", py: 3 }}>
    <Typography variant="body2" color="text.secondary">No smart reports available</Typography>
  </Box>
);

const SmartReportsTable = ({ smartReports, onView, onDownload }) => (
  <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow>
          {["Test Details", "View", "Download"].map((h) => (
            <TableCell key={h} sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {smartReports.map((report) => (
          <TableRow key={`smart-report-${report.id}`}>
            <TableCell>
              <Typography variant="body2" fontWeight="500">{report.testDetail || "Smart Report"}</Typography>
              <Typography variant="caption" color="text.secondary">
                {report.createdDate ? dayjs(report.createdDate).format("DD/MM/YYYY HH:mm") : ""}
              </Typography>
            </TableCell>
            <TableCell>
              <IconButton size="small" onClick={() => onView(report)} color="primary">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </TableCell>
            <TableCell>
              <IconButton size="small" onClick={() => onDownload(report)} color="primary">
                <DownloadOutlinedIcon fontSize="small" />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);
SmartReportsTable.propTypes = {
  smartReports: PropTypes.array.isRequired,
  onView: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
};

const ReportLogsTable = ({ reportsLog }) => (
  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow>
          {["Created Date", "Created By", "Order Status"].map((h) => (
            <TableCell key={h} sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", whiteSpace: "nowrap" }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {reportsLog.map((rec, idx) => (
          <TableRow key={`log-${rec.id || idx}-${rec.labTestCode || idx}`}>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{formatUtcToLocalDate(rec.createdDate)}</TableCell>
            <TableCell>{rec.createdByName || "—"}</TableCell>
            <TableCell>{rec.reportStatus || "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);
ReportLogsTable.propTypes = { reportsLog: PropTypes.array.isRequired };

const PacsStudiesTable = ({ studies, onOpenViewer, onDownloadZip, onViewDetails, onViewDicom }) => (
  <TableContainer component={Paper} sx={{ maxHeight: 350 }}>
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow>
          {["Study / Test Details", "DICOM View"].map((h) => (
            <TableCell key={h} sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", whiteSpace: "nowrap" }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {studies.map((study, idx) => (
          <TableRow key={`pacs-study-${study.id || idx}`} hover>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" fontWeight="600" color="text.primary">
                  {study.testName || study.testCode || "Radiology Study"}
                </Typography>
                {study.hasDicom === 1 && (
                  <Chip
                    label="DICOM"
                    size="small"
                    color="success"
                    sx={{ height: 18, fontSize: "10px", fontWeight: 700 }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 0.5 }}>
                {study.testCode && study.testName !== study.testCode && (
                  <Typography variant="caption" color="text.secondary">
                    Code: <strong>{study.testCode}</strong>
                  </Typography>
                )}
                {study.billId && (
                  <Typography variant="caption" color="text.secondary">
                    Bill ID: <strong>{study.billId}</strong>
                  </Typography>
                )}
                {study.createdDate && (
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(study.createdDate).format("DD/MM/YYYY HH:mm")}
                  </Typography>
                )}
              </Box>
            </TableCell>

            {/* <TableCell sx={{ whiteSpace: "nowrap" }}>
              {study.viewer2dUrl ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  onClick={() => onOpenViewer(study.viewer2dUrl)}
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    fontWeight: 600,
                    borderRadius: "6px",
                    py: 0.3,
                    px: 1,
                    borderColor: "#0284c7",
                    color: "#0369a1",
                    "&:hover": { borderColor: "#0369a1", bgcolor: "#f0f9ff" }
                  }}
                >
                  2D Viewer
                </Button>
              ) : (
                <Typography variant="caption" color="text.secondary">—</Typography>
              )}
            </TableCell>

            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {study.viewer3dUrl ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  onClick={() => onOpenViewer(study.viewer3dUrl)}
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    fontWeight: 600,
                    borderRadius: "6px",
                    py: 0.3,
                    px: 1,
                    borderColor: "#7c3aed",
                    color: "#6d28d9",
                    "&:hover": { borderColor: "#6d28d9", bgcolor: "#f5f3ff" }
                  }}
                >
                  3D Viewer
                </Button>
              ) : (
                <Typography variant="caption" color="text.secondary">—</Typography>
              )}
            </TableCell> */}

            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {study.pacsZipDownloadUrl || study.pacsZipAvailable ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                    onClick={() => onViewDicom(study)}
                    sx={{
                      textTransform: "none",
                      fontSize: "11px",
                      fontWeight: 600,
                      borderRadius: "6px",
                      py: 0.3,
                      px: 1,
                      bgcolor: "#0284c7",
                      "&:hover": { bgcolor: "#0369a1" },
                      boxShadow: "none"
                    }}
                  >
                    View DICOM
                  </Button>
                  {/* <Tooltip title={study.pacsZipObjectKey ? `Download ZIP (${study.pacsZipObjectKey})` : "Download DICOM ZIP Archive"}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onDownloadZip(study.pacsZipDownloadUrl, `${study.testCode || study.id || 'study'}_dicom.zip`)}
                        disabled={!study.pacsZipDownloadUrl}
                        sx={{ color: "#004c97", p: 0.5 }}
                      >
                        <FolderZipIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip> */}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">Unavailable</Typography>
              )}
            </TableCell>

            {/* <TableCell sx={{ whiteSpace: "nowrap" }}>
              <IconButton size="small" onClick={() => onViewDetails(study)} color="info" title="View Study Details">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </TableCell> */}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

PacsStudiesTable.propTypes = {
  studies: PropTypes.array.isRequired,
  onOpenViewer: PropTypes.func.isRequired,
  onDownloadZip: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onViewDicom: PropTypes.func.isRequired,
};

const PacsStudyDetailDialog = ({ open, onClose, study, onOpenViewer, onDownloadZip, onViewDicom }) => {
  if (!study) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 99999 }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Typography variant="h6" fontWeight="bold">PACS Study Details</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Test Name</Typography>
            <Typography variant="body2" fontWeight="600">{study.testName || "—"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Test Code</Typography>
            <Typography variant="body2" fontWeight="600">{study.testCode || "—"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Bill ID</Typography>
            <Typography variant="body2">{study.billId || "—"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Vendor Lab ID</Typography>
            <Typography variant="body2">{study.vendorLabId || "—"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Lab Report ID</Typography>
            <Typography variant="body2">{study.labReportId || "—"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">DICOM Status</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={study.hasDicom === 1 ? "DICOM Available" : "No DICOM"}
                size="small"
                color={study.hasDicom === 1 ? "success" : "default"}
                sx={{ height: 20, fontSize: "11px", fontWeight: 600 }}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Source</Typography>
            <Typography variant="body2">{study.source || "—"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Created Date</Typography>
            <Typography variant="body2">
              {study.createdDate ? dayjs(study.createdDate).format("DD/MM/YYYY HH:mm:ss") : "—"}
            </Typography>
          </Grid>
          {study.pacsZipObjectKey && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">PACS ZIP Object Key (GCS)</Typography>
              <Typography variant="body2" sx={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: "11px", bgcolor: "#f1f5f9", p: 1, borderRadius: "6px" }}>
                {study.pacsZipObjectKey}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        {(study.pacsZipDownloadUrl || study.pacsZipAvailable) && (
          <Button
            variant="contained"
            size="small"
            startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
            onClick={() => { onClose(); onViewDicom(study); }}
            sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#0284c7", "&:hover": { bgcolor: "#0369a1" } }}
          >
            View DICOM
          </Button>
        )}
        {study.viewer2dUrl && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
            onClick={() => onOpenViewer(study.viewer2dUrl)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            2D Viewer
          </Button>
        )}
        {study.viewer3dUrl && (
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
            onClick={() => onOpenViewer(study.viewer3dUrl)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            3D Viewer
          </Button>
        )}
        {study.pacsZipDownloadUrl && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FolderZipIcon sx={{ fontSize: 15 }} />}
            onClick={() => onDownloadZip(study.pacsZipDownloadUrl, `${study.testCode || study.id || 'study'}_dicom.zip`)}
            sx={{ textTransform: "none", fontWeight: 600, color: "#004c97", borderColor: "#004c97" }}
          >
            Download ZIP
          </Button>
        )}
        <Button variant="text" size="small" onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PacsStudyDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  study: PropTypes.object,
  onOpenViewer: PropTypes.func.isRequired,
  onDownloadZip: PropTypes.func.isRequired,
  onViewDicom: PropTypes.func.isRequired,
};

const EmptyState = ({ message }) => (
  <Box sx={{ textAlign: "center", py: 3 }}>
    <Typography variant="body2" color="text.secondary">{message}</Typography>
  </Box>
);
EmptyState.propTypes = { message: PropTypes.string.isRequired };

const RemarksEditMode = ({ remarksValue, setRemarksValue, loading, onCancel, onSave }) => (
  <Box sx={{ width: "70%", display: "flex", flexDirection: "column", gap: 1 }}>
    <TextField
      multiline rows={3} value={remarksValue}
      onChange={(e) => setRemarksValue(e.target.value)}
      variant="outlined" size="small" fullWidth placeholder="Enter remarks..."
    />
    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
      <Button size="small" variant="outlined" onClick={onCancel} disabled={loading}>Cancel</Button>
      <Button size="small" variant="contained" onClick={onSave} disabled={loading}>
        {loading ? "Saving..." : "Save"}
      </Button>
    </Box>
  </Box>
);
RemarksEditMode.propTypes = {
  remarksValue: PropTypes.string.isRequired,
  setRemarksValue: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const RemarksViewMode = ({ remarks, onEdit, isCancelled = false }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "70%" }}>
    <Typography
      variant="body2"
      sx={{
        flex: 1, whiteSpace: "pre-wrap", backgroundColor: "#f1f5f9",
        borderRadius: 1, p: 1.5, minHeight: "40px",
        color: remarks ? "#475569" : "#9ca3af",
      }}
    >
      {remarks || "No remarks added"}
    </Typography>
    {!isCancelled && (
      <IconButton size="small" onClick={onEdit} sx={{ mt: 0.5 }} title="Edit Remarks">
        <EditIcon fontSize="small" />
      </IconButton>
    )}
  </Box>
);
RemarksViewMode.propTypes = {
  remarks: PropTypes.string,
  onEdit: PropTypes.func.isRequired,
  isCancelled: PropTypes.bool,
};

const TRFCell = ({ consignmentId, onGenerateTRF, selectedOrderDetails }) => {
  if (consignmentId) {
    return (
      <Button
        variant="outlined" size="small" startIcon={<PictureAsPdfIcon />}
        onClick={() => onGenerateTRF(selectedOrderDetails)}
        sx={{
          textTransform: "none", borderRadius: "20px",
          borderColor: "#004c97", color: "#004c97",
          "&:hover": { backgroundColor: "rgba(0,76,151,0.04)", borderColor: "#002f5f" },
        }}
      >
        View TRF
      </Button>
    );
  }
  return <Typography variant="body2" color="text.secondary">Not Available</Typography>;
};
TRFCell.propTypes = {
  consignmentId: PropTypes.string,
  onGenerateTRF: PropTypes.func.isRequired,
  selectedOrderDetails: PropTypes.object,
};

const SmartReportStatusDisplay = ({ status }) => (
  <Typography sx={{ color: status === "Sent" ? "green" : "", fontWeight: 600 }}>
    {status === "Sent" ? "Yes" : "No"}
  </Typography>
);
SmartReportStatusDisplay.propTypes = { status: PropTypes.string };

const CCNameValue = ({ orderType, ccName }) => {
  const value = orderType === "Collection Center" ? ccName : "—";
  return <>{value || "—"}</>;
};
CCNameValue.propTypes = { orderType: PropTypes.string, ccName: PropTypes.string };

const UploadReportModal = ({
  open, onClose, selectedOrderDetails, selectedOrder,
  labTestsOrderReports, onViewClick, onDownloadClick, onDeleteClick, onOpenUploadModal,
}) => (
  <Modal open={open} onClose={onClose}>
    <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
      <Card sx={{ maxWidth: "160vh", width: "160vh", maxHeight: "80vh", overflowY: "auto" }}>
        <CardHeader
          title="Upload Report"
          action={<IconButton onClick={onClose}><CloseIcon /></IconButton>}
          sx={{ backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}
        />
        <Divider />
        <CardContent>
          <Box sx={{ mb: 3, borderBottom: "1px solid #e0e0e0", pb: 3, display: "flex", flexDirection: "row" }}>
            <Box sx={{ flex: 1, mr: 2 }}>
              <Typography variant="body1" fontWeight="bold">Patient:</Typography>
              <Typography variant="body1">{getPatientDisplay(selectedOrderDetails)}</Typography>
            </Box>
            <Box sx={{ flex: 1, mr: 2 }}>
              <Typography variant="body1" fontWeight="bold">Mobile Number:</Typography>
              <Typography variant="body1">{selectedOrderDetails?.patientMobile}</Typography>
            </Box>
            <Box sx={{ flex: 1, mr: 2 }}>
              <Typography variant="body1" fontWeight="bold">Order ID:</Typography>
              <Typography variant="body1">{selectedOrder?.id}</Typography>
            </Box>
            <Box sx={{ flex: 1, mr: 2 }}>
              <Typography variant="body1" fontWeight="bold">Billing Date &amp; Time:</Typography>
              <Typography variant="body1">{formatDateToDDMMYYYYHHMM(selectedOrderDetails?.paymentDateTime)}</Typography>
            </Box>
          </Box>
          <Box sx={{ mb: 3, borderBottom: "1px solid #e0e0e0", pb: 3, display: "flex", flexDirection: "row" }}>
            <Box sx={{ flex: 1, mr: 2 }}>
              <Typography variant="body1" fontWeight="bold">Home Sample Collection Address</Typography>
              <Typography variant="body1">{selectedOrder?.originalData?.homeSamplePatientAddress}</Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={onOpenUploadModal}
              disabled={isOrderCancelled(selectedOrder?.orderStatus || selectedOrderDetails?.orderStatus)}
            >
              Upload Report
            </Button>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ marginBottom: "1rem", fontSize: "16px", fontWeight: 500 }}>Lab Report</Typography>
            {labTestsOrderReports.length > 0 ? (
              <ReportTable
                reports={labTestsOrderReports}
                onView={onViewClick}
                onDownload={onDownloadClick}
                onDelete={onDeleteClick}
              />
            ) : (
              <EmptyState message="No reports available" />
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  </Modal>
);
UploadReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedOrderDetails: PropTypes.object,
  selectedOrder: PropTypes.object,
  labTestsOrderReports: PropTypes.array.isRequired,
  onViewClick: PropTypes.func.isRequired,
  onDownloadClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onOpenUploadModal: PropTypes.func.isRequired,
};

const ORDER_INFO_FIELDS = (details) => [
  ["Order ID", details?.orderId || "_"],
  ["Patient Name", details?.patientName || "—"],
  ["Patient Mobile", details?.patientMobile || "—"],
  ["Patient ID", details?.patientId || "—"],
  ["Age", details?.age || "—"],
  ["Gender", getGenderDisplay(details?.gender)],
  ["Consignment ID", details?.consignmentId || "—"],
  ["Consignment Date", details?.consignmentDate || "—"],
  ["Circle Name", details?.circleName || "—"],
  ["Assigned Eclinic", details?.assignedEclinicName || "—"],
  ["Billing Eclinic", details?.eclinicName || "_"],
  ["Lab Test Name", details?.testName || "—"],
  ["Lab Name", details?.labName || "—"],
  ["Test Type", details?.testType || "—"],
];

const OrderInfoFields = ({ selectedOrderDetails, getStatusLabelFromDetails }) => (
  <>
    {ORDER_INFO_FIELDS(selectedOrderDetails).map(([label, value]) => (
      <Grid item xs={12} key={label}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary" fontWeight="500">{label}:</Typography>
          <Typography variant="body2" fontWeight="bold" color="text.primary">{value}</Typography>
        </Box>
      </Grid>
    ))}
    <Grid item xs={12}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary" fontWeight="500">CC Name:</Typography>
        <Typography variant="body2" fontWeight="bold" color="text.primary">
          <CCNameValue orderType={selectedOrderDetails?.orderType} ccName={selectedOrderDetails?.ccName} />
        </Typography>
      </Box>
    </Grid>
    <Grid item xs={12}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary" fontWeight="500">Order Status:</Typography>
        <Chip
          label={selectedOrderDetails?.orderStatus || "—"}
          color={getOrderStatusColor(selectedOrderDetails?.orderStatus)} size="small" variant="filled"
          sx={{
            fontWeight: 600, minWidth: "100px",
            backgroundColor: getOrderStatusColorCode(selectedOrderDetails?.orderStatus),
            color: "white", "& .MuiChip-label": { color: "white" },
          }}
        />
      </Box>
    </Grid>
    <Grid item xs={12}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary" fontWeight="500">Smart Report Status:</Typography>
        <SmartReportStatusDisplay status={selectedOrderDetails?.smartReportStatus} />
      </Box>
    </Grid>
    <Grid item xs={12}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary" fontWeight="500">Sample Status:</Typography>
        <Chip label={getStatusLabelFromDetails(selectedOrderDetails)} color="warning" size="small" sx={{ fontWeight: 600 }} />
      </Box>
    </Grid>
  </>
);
OrderInfoFields.propTypes = {
  selectedOrderDetails: PropTypes.object,
  getStatusLabelFromDetails: PropTypes.func.isRequired,
};

const fetchEclinicList = async () => {
  const base = (process.env.REACT_APP_DIAGNOSTIC_NEW || "").replace(/\/$/, "");
  const url = `${base}/api/v1/mobilab/routed-clinic`;
  const s_token = localStorage.getItem("access_token");
  const res = await axios.get(url, { headers: { Authorization: `Bearer ${s_token}` } });
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

const EclinicReassignSection = ({ selectedOrderDetails, onReassign, loading }) => {
  const [eclinicList, setEclinicList] = useState([]);
  const [selectedEclinic, setSelectedEclinic] = useState(null);
  const [loadingEclinics, setLoadingEclinics] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const isMobilabOrder = useMemo(() => {
    const labName = selectedOrderDetails?.labName?.toLowerCase() || "";
    return labName.includes("mobi");
  }, [selectedOrderDetails?.labName]);

  useEffect(() => {
    if (!isMobilabOrder) {
      setEclinicList([]);
      setSelectedEclinic(null);
      setFetchError(null);
      return;
    }

    let cancelled = false;
    setLoadingEclinics(true);
    setFetchError(null);

    fetchEclinicList()
      .then((list) => {
        if (cancelled) return;
        const safeList = Array.isArray(list) ? list : [];
        console.log('Fetched eClinic list:', safeList);
        setEclinicList(safeList);
        setSelectedEclinic(null);

        if (safeList.length === 0) {
          setFetchError("No eClinics available");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch eClinic list:", err);
        setFetchError("Failed to load eClinics");
        setEclinicList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingEclinics(false);
      });

    return () => { cancelled = true; };
  }, [isMobilabOrder, selectedOrderDetails?.receivedCheId, selectedOrderDetails?.assignedEclinicCode]);

  if (!isMobilabOrder) return null;

  const isCancelled = isOrderCancelledOrRouted(selectedOrderDetails?.orderStatus);

  return (
    <Grid item xs={12}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary" fontWeight="500">
          Assigned eClinic:
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {isCancelled ? (
            <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
              Reassignment not allowed for Cancelled - Routed orders
            </Typography>
          ) : loadingEclinics ? (
            <CircularProgress size={20} />
          ) : fetchError ? (
            <Typography variant="caption" color="error">{fetchError}</Typography>
          ) : eclinicList.length === 0 ? (
            <Typography variant="caption" color="text.secondary">No eClinics available</Typography>
          ) : (
            <>
              <Autocomplete
                size="small"
                sx={{ minWidth: 260, maxWidth: 320 }}
                options={eclinicList}
                value={selectedEclinic || null}
                onChange={(_, newValue) => setSelectedEclinic(newValue)}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  if (typeof option === "string") return option;
                  const name = option.eclinicName || option.hospitalName || option.name || "";
                  const st = option.state || option.stateName || "";
                  return `${name}${st ? ` (${st})` : ""}`;
                }}
                isOptionEqualToValue={(option, val) => {
                  if (!option || !val) return false;
                  const optCode = option.eclinicCode ?? option.hospitalCode ?? option.code ?? option.id;
                  const valCode = val.eclinicCode ?? val.hospitalCode ?? val.code ?? val.id;
                  return String(optCode) === String(valCode);
                }}
                filterOptions={(options, state) => {
                  const query = (state.inputValue || "").trim().toLowerCase();
                  if (!query) return options;
                  return options.filter((o) => {
                    if (!o) return false;
                    const name = String(o.eclinicName || o.hospitalName || o.name || "").toLowerCase();
                    const code = String(o.eclinicCode || o.hospitalCode || o.code || "").toLowerCase();
                    const st = String(o.state || o.stateName || "").toLowerCase();
                    const city = String(o.city || "").toLowerCase();
                    return name.includes(query) || code.includes(query) || st.includes(query) || city.includes(query);
                  });
                }}
                disabled={loading}
                PopperProps={{
                  style: { zIndex: 35000 },
                  sx: { zIndex: 35000 },
                }}
                slotProps={{
                  popper: {
                    style: { zIndex: 35000 },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search eClinic…"
                    size="small"
                  />
                )}
                renderOption={(props, option) => {
                  const name = option.eclinicName || option.hospitalName || option.name || "";
                  const code = option.eclinicCode || option.hospitalCode || option.code || "";
                  const st = option.state || option.stateName || "";
                  return (
                    <li {...props} key={code || name || props.key}>
                      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                          {name}
                        </Typography>
                        {(st || code) && (
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {[code, st].filter(Boolean).join(" • ")}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => onReassign(selectedEclinic)}
                disabled={!selectedEclinic || loading}
                sx={{ textTransform: "none" }}
              >
                Reassign
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Grid>
  );
};
EclinicReassignSection.propTypes = {
  selectedOrderDetails: PropTypes.object,
  onReassign: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

const LabReassignSection = ({
  selectedOrderDetails,
  labOptions,
  loadingLabs,
  onInitiateReassign,
  validating,
  selectedLab,
  setSelectedLab,
}) => {
  const isCancelled = isOrderCancelledOrRouted(selectedOrderDetails?.orderStatus);

  return (
    <Grid item xs={12}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary" fontWeight="500">
          Reassign Lab:
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {isCancelled ? (
            <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
              Reassignment not allowed for Cancelled - Routed orders
            </Typography>
          ) : loadingLabs ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <Autocomplete
                size="small"
                sx={{ minWidth: 260, maxWidth: 320 }}
                options={labOptions}
                value={selectedLab || null}
                onChange={(_, newValue) => setSelectedLab(newValue)}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  if (typeof option === "string") return option;
                  return option.labName ? `${option.labName} (${option.labCode})` : (option.labCode || "");
                }}
                isOptionEqualToValue={(option, val) => {
                  if (!option || !val) return false;
                  return String(option.labCode) === String(val.labCode);
                }}
                filterOptions={(options, state) => {
                  const query = (state.inputValue || "").trim().toLowerCase();
                  if (!query) return options;
                  return options.filter((o) => {
                    if (!o) return false;
                    const name = String(o.labName || "").toLowerCase();
                    const code = String(o.labCode || "").toLowerCase();
                    return name.includes(query) || code.includes(query);
                  });
                }}
                disabled={validating}
                PopperProps={{
                  style: { zIndex: 35000 },
                  sx: { zIndex: 35000 },
                }}
                slotProps={{
                  popper: {
                    style: { zIndex: 35000 },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Lab to Reassign…"
                    size="small"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.labCode || option.labName || props.key}>
                    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                        {option.labName}
                      </Typography>
                      {option.labCode && (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {option.labCode}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => onInitiateReassign(selectedLab)}
                disabled={!selectedLab || validating}
                startIcon={validating ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#004c97",
                  "&:hover": { backgroundColor: "#002f5f" },
                }}
              >
                {validating ? "Validating..." : "Reassign"}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Grid>
  );
};
LabReassignSection.propTypes = {
  selectedOrderDetails: PropTypes.object,
  labOptions: PropTypes.array.isRequired,
  loadingLabs: PropTypes.bool.isRequired,
  onInitiateReassign: PropTypes.func.isRequired,
  validating: PropTypes.bool.isRequired,
  selectedLab: PropTypes.object,
  setSelectedLab: PropTypes.func.isRequired,
};

const ConfirmLabReassignDialog = ({
  open,
  onClose,
  confirmData,
  onConfirm,
  loading,
  onReasonChange,
  errorMessage,
  onClearError,
}) => {
  if (!confirmData) return null;
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth sx={{ zIndex: 36000 }}>
      <DialogTitle sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}>
        <SwapHorizIcon color="primary" />
        Confirm Lab Reassignment
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {errorMessage && (
            <Alert
              severity="error"
              onClose={onClearError}
              sx={{
                width: "100%",
                fontSize: "0.8rem",
                py: 0.5,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                whiteSpace: "normal",
                "& .MuiAlert-message": {
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  whiteSpace: "normal",
                  width: "100%",
                },
              }}
            >
              {errorMessage}
            </Alert>
          )}

          <Box sx={{ bgcolor: "#f8fafc", p: 2, borderRadius: "8px", border: "1px solid #e2e8f0" }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Order ID</Typography>
                <Typography variant="body2" fontWeight="600">{confirmData.orderId || "—"}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Current Lab</Typography>
                <Typography variant="body2" fontWeight="600" color="text.secondary">
                  {confirmData.currentLab || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">New Routed Lab</Typography>
                <Typography variant="body2" fontWeight="600" color="primary">
                  {confirmData.targetLab?.labName || "—"} ({confirmData.targetLab?.labCode || "—"})
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <TextField
            label="Route Reason"
            fullWidth
            multiline
            rows={3}
            size="small"
            value={confirmData.reason || ""}
            onChange={(e) => onReasonChange(e.target.value)}
            disabled={loading}
            placeholder="Enter reason for lab reassignment"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined" size="small">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading || !confirmData.reason?.trim()}
          variant="contained"
          size="small"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            textTransform: "none",
            backgroundColor: "#004c97",
            "&:hover": { backgroundColor: "#002f5f" },
          }}
        >
          {loading ? "Reassigning..." : "Confirm Reassign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
ConfirmLabReassignDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  confirmData: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  onReasonChange: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  onClearError: PropTypes.func,
};

const getCurrentUserCode = () => {
  try {
    const loginObj = JSON.parse(localStorage.getItem("loginObj") || "null");
    if (loginObj?.userSession?.userCode) return loginObj.userSession.userCode;
    if (loginObj?.userCode) return loginObj.userCode;
    const userDetails = JSON.parse(localStorage.getItem("userDetails") || "null");
    if (userDetails?.userSession?.userCode) return userDetails.userSession.userCode;
  } catch (e) {
    console.error("Error reading user code:", e);
  }
  return localStorage.getItem("userCode") || localStorage.getItem("access_name") || "admin";
};

const INITIAL_SEARCH_FILTERS = {
  orderId: "", patientMobile: "", patientName: "",
  fromDate: dayjs().subtract(7, "day"), toDate: dayjs(),
  hospitalName: "", CHEId: "", testType: "All",
  orderStatus: [], selectedEclinics: [], selectedLabs: [], selectedCcs: [], hospitalId: null,
};

export default function CompletedOrders() {
  const location = useLocation();
  const navigate = useNavigate();

  const [orderType, setOrderType] = useState([...ORDER_TYPE_OPTIONS]);
  const [dateRange, setDateRange] = useState("last7");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const { snackbar, showSnackbarMessage, closeSnackbar } = useSnackbar();
  const [reportStatus, setReportStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sort, setSort] = useState({ sortBy: "orderId", sortOrder: "asc" });
  const [reportUploadModal, setReportUploadModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedLabReport, setSelectedLabReport] = useState(null);
  const [typedLabReport, setTypedLabReport] = useState("");
  const [uploadReportsModalOpen, setUploadReportsModalOpen] = useState(false);
  const [patientLabTestsOrderDetails, setPatientLabTestsOrderDetails] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState(0);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [predLabTestCount, setPredLabTestCount] = useState(null);
  const [orderTrackDetails, setOrderTrackDetails] = useState([]);
  const [editingRemarks, setEditingRemarks] = useState(false);
  const [remarksValue, setRemarksValue] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [selectedOrderForStatus, setSelectedOrderForStatus] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);
  const [genaratePDF, setGenaratePDF] = useState(false);
  const [vialsData, setvialsData] = useState();
  const [genaratePDFData, setGenaratePDFData] = useState();
  const [searchFilters, setSearchFilters] = useState(INITIAL_SEARCH_FILTERS);
  const [fetchingMobilabReport, setFetchingMobilabReport] = useState(false);
  const [reassignLabList, setReassignLabList] = useState([]);
  const [loadingReassignLabs, setLoadingReassignLabs] = useState(false);
  const [validatingLab, setValidatingLab] = useState(false);
  const [reassigningLab, setReassigningLab] = useState(false);
  const [selectedLabToReassign, setSelectedLabToReassign] = useState(null);
  const [confirmLabDialog, setConfirmLabDialog] = useState({
    open: false,
    orderId: null,
    targetLab: null,
    currentLab: null,
    reason: "",
  });
  const [confirmLabError, setConfirmLabError] = useState(null);

  const isMobilabOrder = useMemo(() => {
    const labName = selectedOrderDetails?.labName?.toLowerCase() || "";
    return labName.includes("mobi");
  }, [selectedOrderDetails?.labName]);

  const isSearchingRef = useRef(false);
  const isReportDataFetchedRef = useRef(false);
  const initializedRef = useRef(false);
  const dataFetchedRef = useRef(false);
  const hasInitializedQueryParams = useRef(false);
  const isMountedRef = useRef(true);
  const searchFiltersRef = useRef(searchFilters);
  const orderTypeRef = useRef(orderType);
  const pageSizeRef = useRef(pageSize);
  const getDateRangeValuesRef = useRef(null);

  const authToken = localStorage.getItem("access_token");

  const { labList, ccList, eclinicData, setEclinicData, getLabListData, getCcListData, getEclinicsData } = useLabData(
    authToken, searchFilters.testType
  );

  useEffect(() => {
    let isMounted = true;
    const fetchReassignLabs = async () => {
      setLoadingReassignLabs(true);
      try {
        const response = await apiService.getlabsByType({ type: "All" });
        if (response?.status === 200 && isMounted) {
          const list = Array.isArray(response.data) ? response.data : [];
          setReassignLabList(list.filter((l) => l?.labCode && l?.labCode !== "All"));
        }
      } catch (err) {
        console.error("Error loading labs for reassign:", err);
      } finally {
        if (isMounted) setLoadingReassignLabs(false);
      }
    };
    fetchReassignLabs();
    return () => {
      isMounted = false;
    };
  }, []);

  const availableLabOptions = useMemo(() => {
    if (reassignLabList.length > 0) return reassignLabList;
    if (Array.isArray(labList)) {
      return labList.filter((l) => l?.labCode && l?.labCode !== "All");
    }
    return [];
  }, [reassignLabList, labList]);
  const {
    smartReports, setSmartReports, reportsLog, labTestsOrderReports, setLabTestsOrderReports,
    pacsStudies, setPacsStudies, pacsLoading,
    getSmartReports, getReportsLogs, fetchLabReports, getPacsStudies,
    handleDownloadClick, handleSmartReportDownload, handleSmartReportView, handleViewClick,
    handleOpenViewer, handleDownloadPacsZip,
  } = useReportData(showSnackbarMessage);
  const [selectedPacsStudy, setSelectedPacsStudy] = useState(null);
  const [dicomViewerStudy, setDicomViewerStudy] = useState(null);

  useEffect(() => { searchFiltersRef.current = searchFilters; }, [searchFilters]);
  useEffect(() => { orderTypeRef.current = orderType; }, [orderType]);
  useEffect(() => { pageSizeRef.current = pageSize; }, [pageSize]);

  const getDateRangeValues = useCallback(() => {
    const today = dayjs();
    if (dateRange === "last7") return { fromDate: today.subtract(7, "day"), toDate: today };
    if (dateRange === "last30") return { fromDate: today.subtract(30, "day"), toDate: today };
    return { fromDate: searchFilters.fromDate, toDate: searchFilters.toDate };
  }, [dateRange, searchFilters.fromDate, searchFilters.toDate]);

  useEffect(() => { getDateRangeValuesRef.current = getDateRangeValues; }, [getDateRangeValues]);

  const parseQueryParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const paramsObject = {};
    for (const [key, value] of searchParams.entries()) paramsObject[key] = value;
    return paramsObject;
  }, [location.search]);

  const clearLocationParams = useCallback(() => {
    navigate(location.pathname, { replace: true });
    setSearchFilters((prev) => ({ ...prev, fromDate: dayjs().subtract(7, "day"), toDate: dayjs(), hospitalId: null, orderStatus: [] }));
    setDateRange("last7");
  }, [navigate, location.pathname]);

  const fetchOrdersWithParams = useCallback(async (pageNo = 1, customFilters = null) => {
    if (!isMountedRef.current || isSearchingRef.current) return;
    isSearchingRef.current = true;
    setLoading(true);
    try {
      const filtersToUse = customFilters || searchFiltersRef.current;
      const orderTypeValues = orderTypeRef.current.map((opt) => opt.value);
      const payload = buildApiPayload(filtersToUse, pageNo, orderTypeValues, pageSizeRef.current, getDateRangeValuesRef.current);
      const response = await apiService.getOrderDetails(payload);
      if (isMountedRef.current) {
        if (response.status === 200 && response?.data) {
          setOrders(transformOrdersData(response.data.successObject));
          setCurrentPage(pageNo);
          setTotalElements(response.data.totalElements || 0);
          setTotalPages(response.data.totalPages || 0);
        } else {
          setOrders([]); setTotalElements(0); setTotalPages(0);
        }
        setSelectedOrderIds([]); setSelectAll(false);
      }
    } catch (error) {
      console.error("API request error:", error);
      if (isMountedRef.current) { setOrders([]); setTotalElements(0); setTotalPages(0); setSelectedOrderIds([]); setSelectAll(false); }
    } finally {
      if (isMountedRef.current) setLoading(false);
      setTimeout(() => { isSearchingRef.current = false; }, 300);
    }
  }, []);

  const fetchOrders = useCallback((pageNo = 1) => fetchOrdersWithParams(pageNo, searchFiltersRef.current), [fetchOrdersWithParams]);

  const applyQueryParams = useCallback(() => {
    if (hasInitializedQueryParams.current || !isMountedRef.current) return;
    const queryParams = parseQueryParams();
    hasInitializedQueryParams.current = true;
    if (Object.keys(queryParams).length === 0) { fetchOrdersWithParams(1); return; }
    const updatedFilters = mapQueryParamsToFilters(queryParams, INITIAL_SEARCH_FILTERS, setOrderType);
    setSearchFilters(updatedFilters);
    fetchOrdersWithParams(1, {
      ...updatedFilters,
      fromDate: formatDateToMMDDYYYY(updatedFilters.fromDate),
      toDate: formatDateToMMDDYYYY(updatedFilters.toDate),
    });
  }, [parseQueryParams, fetchOrdersWithParams]);

  const fetchInitialData = useCallback(async () => {
    if (dataFetchedRef.current || !isMountedRef.current) return;
    setOrderType([...ORDER_TYPE_OPTIONS]);
    dataFetchedRef.current = true;
  }, []);

  useEffect(() => { return () => { isMountedRef.current = false; }; }, []);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      fetchInitialData().then(() => applyQueryParams());
    }
  }, [fetchInitialData, applyQueryParams]);

  const isFirstPageSizeRender = useRef(true);
  useEffect(() => {
    if (isFirstPageSizeRender.current) { isFirstPageSizeRender.current = false; return; }
    fetchOrdersWithParams(1);
  }, [pageSize, fetchOrdersWithParams]);

  useEffect(() => { getLabListData(true, setSearchFilters); }, [searchFilters.testType]);
  useEffect(() => { getCcListData(); }, []);

  const getSelectableOrders = useCallback(() => orders.filter((o) => o.hasReport), [orders]);

  const handleSelectOrder = (orderId) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order?.hasReport) return;
    setSelectedOrderIds((prev) => {
      const newSelected = prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId];
      const selectable = getSelectableOrders();
      setSelectAll(selectable.length > 0 && selectable.every((o) => newSelected.includes(o.id)));
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    const selectable = getSelectableOrders();
    if (selectAll) { setSelectedOrderIds([]); setSelectAll(false); }
    else { setSelectedOrderIds(selectable.map((o) => o.id)); setSelectAll(true); }
  };

  const handleOrderStatusChange = (newValue) =>
    setSearchFilters((prev) => ({ ...prev, orderStatus: newValue.map((v) => v.value) }));
  const handleOrderTypeChange = (newValue) => setOrderType(newValue);

  const handleBulkDownload = useCallback(async () => {
    if (selectedOrderIds.length === 0) { showSnackbarMessage("Please select at least one order", "warning"); return; }
    setBulkDownloadLoading(true);
    try {
      const response = await apiService.reportBulkDownload({ orderIds: selectedOrderIds.join(",") });
      if (!response) throw new Error("No data received");
      const url = globalThis.URL.createObjectURL(new Blob([response], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url; link.download = `bulk_reports_${Date.now()}.zip`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => globalThis.URL.revokeObjectURL(url), 100);
      showSnackbarMessage("Bulk download started successfully");
    } catch (error) {
      console.error("Bulk download failed:", error);
      showSnackbarMessage(`Bulk download failed: ${error.response?.data?.message || error.message}`, "error");
    } finally { setBulkDownloadLoading(false); }
  }, [selectedOrderIds, showSnackbarMessage]);

  const exportToExcel = useCallback(async () => {
    setLoading(true);
    try {
      const payload = buildApiPayload(searchFiltersRef.current, 1, orderTypeRef.current.map((o) => o.value), pageSizeRef.current, getDateRangeValuesRef.current);
      delete payload.pageNumber; delete payload.pageSize;
      const response = await apiService.exportOrders(payload);
      if (!response) throw new Error("No data received");
      const url = globalThis.URL.createObjectURL(new Blob([response], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const link = document.createElement("a");
      link.href = url; link.download = `orders_${Date.now()}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => globalThis.URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error("Export failed:", error);
      showSnackbarMessage(`Export failed: ${error.response?.data?.message || error.message}`, "error");
    } finally { setLoading(false); }
  }, [showSnackbarMessage]);

  const ongenarateTRF = async (lab) => {
    try {
      setLoading(true);
      const orderDetails = await apiService.getLabOrderDetails({ orderId: lab.orderId, isCategory: "Y" });
      setvialsData(Object.values(orderDetails.data));
      const trfResponse = await axios.get(
        `${process.env.REACT_APP_NEW}GenerateTrf/list/getReport?orderId=${lab.orderId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (trfResponse?.data?.data) { setGenaratePDFData(trfResponse.data.data); setGenaratePDF(true); }
      else showSnackbarMessage("Kindly contact the eClinic staff to proceed with TRF generation.", "error");
    } catch (error) { console.error("TRF generation error:", error); }
    finally { setLoading(false); }
  };

  const handleReassignEclinic = useCallback(async (selectedEclinic) => {
    if (isOrderCancelledOrRouted(selectedOrderDetails?.orderStatus || selectedOrder?.orderStatus)) {
      showSnackbarMessage("Cannot reassign eClinic for Cancelled - Routed orders", "warning");
      return;
    }
    if (!selectedEclinic || !selectedOrderDetails?.orderId) {
      showSnackbarMessage("Please select an eClinic to reassign", "error");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        orderId: selectedOrderDetails.orderId,
        receivedCheId: selectedEclinic.eclinicCode,
      };
      const response = await apiService.reassignEclinic(payload);
      const result = response?.data;

      if (result?.status === true) {
        showSnackbarMessage(result?.message || "eClinic reassigned successfully");
        setSelectedOrderDetails((prev) => ({
          ...prev,
          receivedCheId: selectedEclinic.eclinicCode,
          assignedEclinicName: selectedEclinic.eclinicName,
        }));
        setOrders((prev) =>
          prev.map((o) =>
            o.id === selectedOrderDetails.orderId
              ? {
                ...o,
                assignedEclinicName: selectedEclinic.eclinicName,
                originalData: {
                  ...o.originalData,
                  receivedCheId: selectedEclinic.eclinicCode,
                  assignedEclinicName: selectedEclinic.eclinicName,
                },
              }
              : o
          )
        );
        fetchOrders(currentPage);
      } else {
        showSnackbarMessage(result?.message || "Failed to reassign eClinic", "error");
      }
    } catch (error) {
      console.error("Error reassigning eClinic:", error);
      showSnackbarMessage(error?.response?.data?.message || "Error reassigning eClinic", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedOrderDetails, selectedOrder, currentPage, fetchOrders, showSnackbarMessage]);

  const handleFetchMobilabReport = useCallback(async () => {
    if (isOrderCancelled(selectedOrderDetails?.orderStatus || selectedOrder?.orderStatus)) {
      showSnackbarMessage("Cannot fetch report for Cancelled orders", "warning");
      return;
    }
    const orderId = selectedOrderDetails?.orderId || selectedOrder?.id;
    if (!orderId) {
      showSnackbarMessage("Order ID not available", "error");
      return;
    }
    setFetchingMobilabReport(true);
    try {
      const response = await apiService.fetchMobilabReportPdf({ orderId: String(orderId) });
      const result = response?.data;
      const isSuccess =
        response?.status === 200 ||
        response?.status === 201 ||
        response?.status === 202 ||
        result?.status === true ||
        result?.status === 200 ||
        result?.responseCode === "200" ||
        result?.responseCode === "202" ||
        result?.success === true;

      if (isSuccess) {
        showSnackbarMessage(result?.message || result?.responseMessage || "Report fetched successfully");
      } else {
        showSnackbarMessage(result?.message || result?.responseMessage || "Failed to fetch report", "error");
      }
    } catch (error) {
      console.error("Error fetching Mobilab report:", error);
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.responseMessage ||
        error?.message ||
        "Failed to fetch report";
      showSnackbarMessage(errMsg, "error");
    } finally {
      await fetchLabReports(orderId);
      await getReportsLogs(orderId);
      fetchOrders(currentPage);
      setFetchingMobilabReport(false);
    }
  }, [selectedOrderDetails?.orderStatus, selectedOrderDetails?.orderId, selectedOrder?.orderStatus, selectedOrder?.id, showSnackbarMessage, fetchLabReports, getReportsLogs, fetchOrders, currentPage]);

  const handleValidateLabReassign = useCallback(async (targetLab) => {
    if (isOrderCancelledOrRouted(selectedOrderDetails?.orderStatus || selectedOrder?.orderStatus)) {
      showSnackbarMessage("Cannot reassign lab for Cancelled - Routed orders", "warning");
      return;
    }
    const orderId = selectedOrderDetails?.orderId || selectedOrder?.id;
    if (!orderId) {
      showSnackbarMessage("Order ID not available", "error");
      return;
    }
    if (!targetLab?.labCode) {
      showSnackbarMessage("Please select a lab to reassign", "error");
      return;
    }

    setValidatingLab(true);
    try {
      const payload = {
        orderId: String(orderId),
        routedLabCode: String(targetLab.labCode),
      };
      const response = await apiService.validateLabRouting(payload);
      const resData = response?.data;
      if (response?.status === 200 && resData?.status !== false && resData?.valid !== false && resData?.success !== false) {
        setConfirmLabError(null);
        setConfirmLabDialog({
          open: true,
          orderId: String(orderId),
          targetLab,
          currentLab: selectedOrderDetails?.labName || selectedOrder?.labName || "—",
          reason: "",
        });
      } else {
        const errMsg = resData?.message || resData?.responseMessage || "Validation failed for selected lab.";
        showSnackbarMessage(errMsg, "error");
      }
    } catch (error) {
      console.error("Lab routing validation error:", error);
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.responseMessage ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to validate lab routing. Please try again.";
      showSnackbarMessage(errMsg, "error");
    } finally {
      setValidatingLab(false);
    }
  }, [selectedOrderDetails, selectedOrder, showSnackbarMessage]);

  const handleConfirmLabReassign = useCallback(async () => {
    setConfirmLabError(null);
    if (isOrderCancelledOrRouted(selectedOrderDetails?.orderStatus || selectedOrder?.orderStatus)) {
      setConfirmLabError("Cannot reassign lab for Cancelled - Routed orders");
      showSnackbarMessage("Cannot reassign lab for Cancelled - Routed orders", "warning");
      return;
    }
    const { orderId, targetLab, reason } = confirmLabDialog;
    if (!orderId || !targetLab?.labCode) {
      setConfirmLabError("Missing order or lab information");
      showSnackbarMessage("Missing order or lab information", "error");
      return;
    }
    if (!reason?.trim()) {
      setConfirmLabError("Please enter a route reason");
      showSnackbarMessage("Please enter a route reason", "error");
      return;
    }

    setReassigningLab(true);
    try {
      const userCode = getCurrentUserCode();
      const payload = {
        orderId: String(orderId),
        routedLabCode: String(targetLab.labCode),
        routeReason: reason.trim(),
        initiatedBy: userCode,
      };
      const response = await apiService.requestLabRouting(payload);
      const result = response?.data;
      const isSuccess =
        response?.status === 200 ||
        response?.status === 201 ||
        result?.status === true ||
        result?.responseCode === "200" ||
        result?.success === true;

      if (isSuccess && result?.status !== false && result?.success !== false) {
        const succMsg = result?.message || result?.responseMessage || "Lab reassigned successfully";
        showSnackbarMessage(succMsg);
        setSelectedOrderDetails((prev) => (prev ? {
          ...prev,
          labCode: targetLab.labCode,
          labName: targetLab.labName || targetLab.labCode,
        } : prev));
        setOrders((prev) =>
          prev.map((o) =>
            String(o.id) === String(orderId)
              ? {
                  ...o,
                  labName: targetLab.labName || targetLab.labCode,
                  originalData: {
                    ...o.originalData,
                    labCode: targetLab.labCode,
                    labName: targetLab.labName || targetLab.labCode,
                  },
                }
              : o
          )
        );
        fetchOrders(currentPage);
        setConfirmLabDialog({
          open: false,
          orderId: null,
          targetLab: null,
          currentLab: null,
          reason: "",
        });
        setSelectedLabToReassign(null);
        setConfirmLabError(null);
      } else {
        const errMsg = result?.message || result?.responseMessage || "Failed to reassign lab";
        setConfirmLabError(errMsg);
        showSnackbarMessage(errMsg, "error");
      }
    } catch (error) {
      console.error("Lab routing request error:", error);
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.responseMessage ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to reassign lab. Please try again.";
      setConfirmLabError(errMsg);
      showSnackbarMessage(errMsg, "error");
    } finally {
      setReassigningLab(false);
    }
  }, [confirmLabDialog, selectedOrderDetails, selectedOrder, currentPage, fetchOrders, showSnackbarMessage]);

  const handleCloseConfirmLabDialog = useCallback(() => {
    if (reassigningLab) return;
    setConfirmLabError(null);
    setConfirmLabDialog((prev) => ({ ...prev, open: false }));
  }, [reassigningLab]);

  const handleCancelOrder = useCallback(async (order) => {
    setLoading(true);
    try {
      const loginObj = JSON.parse(localStorage.getItem("loginObj") || "null");
      const userCode = loginObj?.userSession?.userCode || "admin";
      const response = await apiService.updateOrderStatus({ orderId: order.id, orderStatus: "Cancelled", updatedBy: userCode });
      if (response.data?.responseCode === "200") {
        showSnackbarMessage(response.data.responseMessage);
        setCancelDialogOpen(false); setOrderToCancel(null);
        fetchOrders(currentPage);
      } else showSnackbarMessage("Failed to cancel order", "error");
    } catch (error) {
      console.error("Error cancelling order:", error);
      showSnackbarMessage("Error cancelling order", "error");
    } finally { setLoading(false); }
  }, [currentPage, fetchOrders, showSnackbarMessage]);

  const openCancelDialog = useCallback((order) => { setOrderToCancel(order); setCancelDialogOpen(true); setStatusMenuAnchor(null); }, []);
  const closeCancelDialog = useCallback(() => { setCancelDialogOpen(false); setOrderToCancel(null); }, []);
  const handleStatusClick = useCallback((event, order) => {
    if (isOrderCancelledOrRouted(order?.orderStatus)) return;
    const statusStr = (order?.orderStatus || "").toLowerCase();
    if (statusStr === "completed" || statusStr === "complete") {
      return;
    }
    setStatusMenuAnchor(event.currentTarget);
    setSelectedOrderForStatus(order);
  }, []);
  const handleStatusMenuClose = useCallback(() => { setStatusMenuAnchor(null); setSelectedOrderForStatus(null); }, []);

  const handleEditClick = useCallback((order) => {
    if (isOrderCancelled(order?.orderStatus)) {
      showSnackbarMessage("Cannot edit Booking ID for Cancelled orders", "warning");
      return;
    }
    setEditingId(order.id);
    setEditValue(order.labConfirmId || "");
  }, [showSnackbarMessage]);
  const handleCancelEdit = useCallback(() => { setEditingId(null); setEditValue(""); }, []);

  const handleSaveBookingId = useCallback(async (order) => {
    const status = order?.orderStatus || order?.originalData?.orderStatus;
    if (isOrderCancelled(status)) {
      showSnackbarMessage("Cannot edit Booking ID for Cancelled orders", "warning");
      return;
    }
    if (!editValue.trim()) { showSnackbarMessage("Please enter a valid Booking ID", "error"); return; }
    try {
      setLoading(true);
      const targetOrderId = order?.id || order?.orderId;
      const response = await apiService.updateManualId({ orderId: targetOrderId, externalLabConfirmId: editValue });
      if (response.data?.successCount === 1) {
        setOrders((prev) => (Array.isArray(prev) ? prev.map((item) =>
          String(item.id) === String(targetOrderId) || String(item.orderId) === String(targetOrderId)
            ? {
                ...item,
                labConfirmId: editValue,
                originalData: {
                  ...(item.originalData || {}),
                  extLabConfirmId: editValue,
                  extLabConfirmIdStatus: "M",
                },
              }
            : item
        ) : []));

        if (targetOrderId) {
          setSelectedOrderDetails((prev) => {
            if (!prev) return prev;
            if (String(prev.orderId) === String(targetOrderId) || String(prev.id) === String(targetOrderId)) {
              return { ...prev, extLabConfirmId: editValue, extLabConfirmIdStatus: "M" };
            }
            return prev;
          });

          setSelectedOrder((prev) => {
            if (!prev) return prev;
            if (String(prev.id) === String(targetOrderId) || String(prev.orderId) === String(targetOrderId)) {
              return {
                ...prev,
                labConfirmId: editValue,
                originalData: {
                  ...(prev.originalData || {}),
                  extLabConfirmId: editValue,
                  extLabConfirmIdStatus: "M",
                },
              };
            }
            return prev;
          });
        }

        setEditingId(null); setEditValue("");
        showSnackbarMessage("Booking ID updated successfully");
        fetchOrders(currentPage);
      } else showSnackbarMessage("Failed to update Booking ID", "error");
    } catch (error) {
      console.error("Error updating booking ID:", error);
      showSnackbarMessage("Error updating booking ID", "error");
    } finally { setLoading(false); }
  }, [currentPage, editValue, fetchOrders, showSnackbarMessage]);

  const handleEditRemarksClick = useCallback(() => {
    if (isOrderCancelled(selectedOrderDetails?.orderStatus || selectedOrder?.orderStatus)) {
      showSnackbarMessage("Cannot add or edit remarks for Cancelled orders", "warning");
      return;
    }
    setEditingRemarks(true);
    setRemarksValue(selectedOrderDetails?.remarks || "");
  }, [selectedOrderDetails?.orderStatus, selectedOrderDetails?.remarks, selectedOrder?.orderStatus, showSnackbarMessage]);
  const handleCancelRemarksEdit = useCallback(() => { setEditingRemarks(false); setRemarksValue(selectedOrderDetails?.remarks || ""); }, [selectedOrderDetails?.remarks]);

  const handleSaveRemarks = useCallback(async () => {
    if (isOrderCancelled(selectedOrderDetails?.orderStatus || selectedOrder?.orderStatus)) {
      showSnackbarMessage("Cannot add or edit remarks for Cancelled orders", "warning");
      return;
    }
    try {
      setLoading(true);
      const loginObj = JSON.parse(localStorage.getItem("loginObj") || "null");
      const userCode = loginObj?.userSession?.userCode || "admin";
      const response = await apiService.updateRemarks({ orderId: selectedOrderDetails?.orderId, remarks: remarksValue, modifiedBy: userCode });
      if (response.data.successObject) {
        showSnackbarMessage("Remarks updated successfully");
        setSelectedOrderDetails((prev) => (prev ? { ...prev, remarks: remarksValue } : prev));
        setOrders((prev) => (Array.isArray(prev) ? prev.map((o) =>
          o.id === selectedOrderDetails?.orderId ? { ...o, originalData: { ...(o.originalData || {}), remarks: remarksValue } } : o
        ) : []));
        setEditingRemarks(false); fetchOrders(currentPage);
      } else showSnackbarMessage("Failed to update remarks", "error");
    } catch (error) {
      console.error("Error updating remarks:", error);
      showSnackbarMessage("Error updating remarks", "error");
    } finally { setLoading(false); }
  }, [currentPage, fetchOrders, remarksValue, selectedOrder, selectedOrderDetails, showSnackbarMessage]);

  const handleUploadClick = useCallback(async (order) => {
    if (order?.b2b2cType === "Y") return;
    if (isOrderCancelled(order?.orderStatus)) {
      showSnackbarMessage("Cannot upload report for Cancelled orders", "warning");
      return;
    }
    setSelectedOrderDetails(order?.originalData || order);
    if (!order?.labConfirmId) { showSnackbarMessage("Please enter Manual Booking ID first", "error"); return; }
    setSelectedOrder(order);
    setLoading(true);
    try {
      const labRes = await apiService.getLabTestsOrderReport({ orderId: order?.id, status: 1, docmentRequired: "Y" });
      if (labRes?.data.responseCode === "200" && labRes?.data?.successList) setLabTestsOrderReports(labRes.data.successList);
    } catch (error) { console.error("Error fetching lab tests order report:", error); }
    try {
      const detailsRes = await apiService.getPatientLabTestsOrderDetails(order?.id);
      if (detailsRes?.status === 200 && detailsRes?.data) setPatientLabTestsOrderDetails(detailsRes.data.successList);
    } catch (error) { console.error("Error fetching patient lab tests order details:", error); }
    setLoading(false);
    setReportUploadModal(true);
  }, [showSnackbarMessage, setLabTestsOrderReports]);

  const handleCloseModal = useCallback(() => {
    setReportUploadModal(false); setSelectedOrder(null); setLabTestsOrderReports([]); setPatientLabTestsOrderDetails([]);
  }, [setLabTestsOrderReports]);

  const handleDeleteClick = useCallback(async (rec) => {
    setLoading(true);
    const loginObj = JSON.parse(localStorage.getItem("loginObj") || "null");
    const userCode = loginObj?.userSession?.userCode || null;
    const currentDateTime = dayjs().format("YYYY-MM-DD HH:mm:ss");
    const payload = [{
      createdBy: userCode, createdDate: currentDateTime, id: rec?.id,
      labOrderDoc: rec?.labOrderDoc || "", labTestCode: rec?.labTestCode, labTestName: rec?.labTestName,
      modifiedBy: userCode, modifiedDate: currentDateTime, orderId: selectedOrder?.id,
      status: 0, type: rec?.type,
    }];
    try {
      const res = await apiService.uploadLabReports(payload, "PUT");
      if (res?.data.responseCode === "200") {
        showSnackbarMessage("Your file Deleted successfully.");
        setLabTestsOrderReports([]);
        await fetchLabReports(selectedOrder?.id);
      } else showSnackbarMessage("Please try after sometime", "error");
    } catch (error) {
      console.error("Error Deleting the Lab Test Record", error);
      showSnackbarMessage("Error deleting file", "error");
    } finally { setLoading(false); }
  }, [selectedOrder?.id, showSnackbarMessage, fetchLabReports, setLabTestsOrderReports]);

  const handleSaveUpload = useCallback(async () => {
    const validationError = validateUpload(selectedFiles, reportStatus);
    if (validationError) { showSnackbarMessage(validationError, "error"); return; }
    let base64Strings = [];
    if (selectedFiles.length > 0) {
      try { base64Strings = await encodeBase64Files(selectedFiles); }
      catch (error) { console.error("Error encoding files:", error); showSnackbarMessage("Error processing files", "error"); return; }
    }
    const loginObj = JSON.parse(localStorage.getItem("loginObj") || "null");
    const userCode = loginObj?.userSession?.userCode || null;
    const currentDateTime = dayjs().format("YYYY-MM-DD HH:mm:ss");
    const payload = buildUploadPayload({ files: selectedFiles, base64Strings, userCode, currentDateTime, selectedLabReport, selectedOrder, reportStatus });
    setLoading(true);
    try {
      const res = await apiService.uploadLabReports(payload, "POST");
      if (res?.data?.responseCode == '202') {
        showSnackbarMessage(res?.data?.responseMessage);
        setReportStatus(null); setSelectedFiles([]); setSelectedLabReport(null); setLabTestsOrderReports([]);
        const labRes = await apiService.getLabTestsOrderReport({ orderId: selectedOrder?.id, status: 1, docmentRequired: "Y" });
        if (labRes?.data?.responseCode === "200" && labRes?.data?.successList) setLabTestsOrderReports(labRes.data.successList);
        setOrders((prev) => (Array.isArray(prev) ? prev.map((o) =>
          o.id === selectedOrder?.id
            ? { ...o, reportAvailable: 1, reportStatus: 2, status: "Complete", originalData: { ...(o.originalData || {}), status: 2, reportAvailable: 1 }, hasReport: true }
            : o
        ) : []));
        setUploadReportsModalOpen(false);
        fetchOrdersWithParams(currentPage);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      showSnackbarMessage("Error uploading files", "error");
    } finally { setLoading(false); }
  }, [selectedFiles, reportStatus, selectedLabReport, selectedOrder, currentPage, showSnackbarMessage, fetchOrdersWithParams, setLabTestsOrderReports]);

  const getOrderTrack = useCallback(async (orderId) => {
    try {
      setLoading(true);
      const response = await apiService.gettrackStatuses({ orderId });
      const updatedDetails = ORDER_TRACKING_DETAILS.map((item) => {
        const match = response.data?.find((apiItem) => apiItem.sampleStatusId === item.sampleStatusId);
        return match ? { ...item, ...match } : item;
      });
      setOrderTrackDetails(updatedDetails);
      return updatedDetails;
    } catch (error) { console.error("Error fetching order track:", error); return ORDER_TRACKING_DETAILS; }
    finally { setLoading(false); }
  }, []);

  const handleOrderIdClick = useCallback(async (order) => {
    if (!order) return;
    setSelectedOrder(order);
    setSelectedOrderDetails(order.originalData || order);
    setEditValue(order.labConfirmId || order.originalData?.extLabConfirmId || "");
    setRemarksValue(order.originalData?.remarks || "");
    setSelectedLabToReassign(null);
    setSidebarTab(0);
    setPredLabTestCount(null);
    const trackingData = await getOrderTrack(order.id);
    setOrderTrackDetails(trackingData);
    await getSmartReports(order.id);
    setSidebarOpen(true);
    await fetchLabReports(order.id);
    await getReportsLogs(order.id);
    await getPacsStudies(order.id);
  }, [getOrderTrack, getSmartReports, fetchLabReports, getReportsLogs, getPacsStudies]);

  const handleCloseSidebar = useCallback(() => {
    setSidebarOpen(false); setSelectedOrderDetails(null); setOrderTrackDetails([]);
    setSidebarTab(0);
    setPredLabTestCount(null);
    setSelectedLabToReassign(null);
    setConfirmLabDialog({
      open: false,
      orderId: null,
      targetLab: null,
      currentLab: null,
      reason: "",
    });
    setConfirmLabError(null);
    setEditingRemarks(false); setSmartReports([]); setLabTestsOrderReports([]);
    setPacsStudies([]); setSelectedPacsStudy(null); setDicomViewerStudy(null);
  }, [setSmartReports, setLabTestsOrderReports, setPacsStudies]);

  const handleSearch = useCallback(() => {
    if (isSearchingRef.current) return;
    const dateRangeValues = getDateRangeValuesRef.current();
    const updatedFilters = { ...searchFiltersRef.current, fromDate: dateRangeValues.fromDate, toDate: dateRangeValues.toDate };
    setSearchFilters(updatedFilters);
    fetchOrdersWithParams(1, updatedFilters);
    isReportDataFetchedRef.current = false;
  }, [fetchOrdersWithParams]);

  const handleReset = useCallback(() => {
    if (isSearchingRef.current) return;
    clearLocationParams();
    setOrderType([...ORDER_TYPE_OPTIONS]);
    setDateRange("last7");
    setSearchFilters(INITIAL_SEARCH_FILTERS);
    setPageSize(10);
    setLabTestsOrderReports([]);
    dataFetchedRef.current = false;
    hasInitializedQueryParams.current = false;
    fetchOrdersWithParams(1, INITIAL_SEARCH_FILTERS);
    isReportDataFetchedRef.current = false;
    setEclinicData([]);
  }, [clearLocationParams, fetchOrdersWithParams, setEclinicData, setLabTestsOrderReports]);

  const handleSort = useCallback((column) => {
    setSort((prev) => ({ sortBy: column, sortOrder: prev.sortBy === column && prev.sortOrder === "asc" ? "desc" : "asc" }));
  }, []);

  const handlePageSizeChange = useCallback((event) => setPageSize(event.target.value), []);

  const handleDateRangeChange = useCallback((value) => {
    setDateRange(value);
    setSearchFilters((prev) => {
      if (value === "custom") return { ...prev, fromDate: normalizeToDayjs(prev.fromDate), toDate: normalizeToDayjs(prev.toDate) };
      const { fromDate, toDate } = getDateRangeValues();
      return { ...prev, fromDate, toDate };
    });
  }, [getDateRangeValues]);

  useEffect(() => {
    if (selectedOrder) setEditValue(selectedOrder.labConfirmId || selectedOrder.originalData?.extLabConfirmId || "");
  }, [selectedOrder]);

  const orderTypeValuesSet = useMemo(() => new Set(orderType.map((opt) => opt.value)), [orderType]);
  const shouldShowEclinicFilter = orderTypeValuesSet.has("All") || orderTypeValuesSet.has("Lab") || orderTypeValuesSet.has("Collection Center") || orderTypeValuesSet.has("b2b2C");
  const shouldShowLabFilter = orderTypeValuesSet.has("All") || orderTypeValuesSet.has("Lab") || orderTypeValuesSet.has("d2c") || orderTypeValuesSet.has("Collection Center") || orderTypeValuesSet.has("b2b2C");
  const shouldShowCcFilter = orderTypeValuesSet.has("All") || orderTypeValuesSet.has("Collection Center") || orderTypeValuesSet.has("b2b2C");

  const getStatusLabelFromDetails = useMemo(() => (order) => {
    const status = ORDER_TRACKING_DETAILS.find((item) => item.sampleStatusId === order?.sampleStatus);
    return status ? status.sampleStatus : "Unknown Status";
  }, []);

  const selectableOrders = getSelectableOrders();
  const isAllSelected = selectAll && selectedOrderIds.length === selectableOrders.length && selectableOrders.length > 0;
  const isIndeterminate = selectedOrderIds.length > 0 && selectedOrderIds.length < selectableOrders.length;

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchFilters.orderId?.trim()) count++;
    if (searchFilters.patientMobile?.trim()) count++;
    if (searchFilters.patientName?.trim()) count++;
    if (searchFilters.testType && searchFilters.testType !== "All") count++;
    if (searchFilters.orderStatus && searchFilters.orderStatus.length > 0 && !searchFilters.orderStatus.includes("All")) count += searchFilters.orderStatus.length;
    if (searchFilters.selectedEclinics?.length > 0) count += searchFilters.selectedEclinics.length;
    if (searchFilters.selectedLabs?.length > 0) count += searchFilters.selectedLabs.length;
    if (searchFilters.selectedCcs?.length > 0) count += searchFilters.selectedCcs.length;
    if (orderType?.length > 0 && !orderType.some((o) => o.value === "All")) count += orderType.length;
    return count;
  }, [searchFilters, orderType]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          height: { xs: "auto", md: "calc(100vh - 64px)" },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          pt: 0.75,
          pb: 0.5,
          px: 0,
          bgcolor: "#F8FBFF",
          boxSizing: "border-box",
        }}
      >
        {loading && (
          <Box position="fixed" top={0} left={0} width="100%" height="100%" bgcolor="rgba(0,0,0,0.1)" zIndex={999} display="flex" justifyContent="center" alignItems="center">
            <CircularProgress color="secondary" />
          </Box>
        )}

        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "#F8FBFF",
            pb: 0.5,
            mb: 0.5,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F172A", letterSpacing: "-0.01em", fontSize: "1.15rem" }}>
                Orders
              </Typography>
              {totalElements != null && totalElements > 0 && (
                <Chip
                  label={`${Number(totalElements).toLocaleString()} Total`}
                  size="small"
                  sx={{
                    bgcolor: "#F1F5F9",
                    color: "#475569",
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    height: 22,
                    borderRadius: "5px",
                  }}
                />
              )}
              {activeFilterCount > 0 && (
                <Chip
                  label={`${activeFilterCount} applied`}
                  size="small"
                  onDelete={handleReset}
                  sx={{
                    bgcolor: "#EFF6FF",
                    color: "#004c97",
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    height: 22,
                    borderRadius: "5px",
                    "& .MuiChip-deleteIcon": { color: "#004c97", fontSize: 14 },
                  }}
                />
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 145 }}>
                <Select
                  value={dateRange}
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                  sx={{
                    height: 32,
                    borderRadius: "7px",
                    bgcolor: "#ffffff",
                    fontSize: "0.8125rem",
                    "& .MuiSelect-select": { py: "4px !important" },
                    "& fieldset": { borderColor: "#CBD5E1" },
                    "&:hover fieldset": { borderColor: "#94A3B8" },
                  }}
                >
                  {DATE_RANGE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: "0.8125rem" }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {dateRange === "custom" && (
                <Box display="flex" alignItems="center" gap={1}>
                  <DatePicker
                    label="From Date"
                    value={searchFilters.fromDate}
                    onChange={(v) => setSearchFilters((p) => ({ ...p, fromDate: v }))}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: 130,
                          "& .MuiOutlinedInput-root": { height: 32, borderRadius: "7px", fontSize: "0.8125rem", bgcolor: "#fff" },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label="To Date"
                    value={searchFilters.toDate}
                    onChange={(v) => setSearchFilters((p) => ({ ...p, toDate: v }))}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: 130,
                          "& .MuiOutlinedInput-root": { height: 32, borderRadius: "7px", fontSize: "0.8125rem", bgcolor: "#fff" },
                        },
                      },
                    }}
                  />
                </Box>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  color: "#004c97",
                  bgcolor: "#ffffff",
                  borderColor: "#CBD5E1",
                  borderRadius: "7px",
                  height: 32,
                  px: 1.5,
                  "&:hover": { bgcolor: "#EFF6FF", borderColor: "#004c97" },
                }}
                onClick={exportToExcel}
                disabled={loading}
              >
                Export to Excel
              </Button>
              {selectedOrderIds.length > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={bulkDownloadLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.8125rem",
                    bgcolor: "#004c97",
                    borderRadius: "7px",
                    height: 32,
                    px: 1.5,
                    "&:hover": { bgcolor: "#002f5f" },
                  }}
                  onClick={handleBulkDownload}
                  disabled={bulkDownloadLoading}
                >
                  {bulkDownloadLoading ? "Downloading..." : `Download Reports (${selectedOrderIds.length})`}
                </Button>
              )}
            </Box>
          </Box>

          <FilterSection
            orderType={orderType} onOrderTypeChange={handleOrderTypeChange}
            searchFilters={searchFilters} setSearchFilters={setSearchFilters}
            eclinicData={eclinicData} setEclinicData={setEclinicData} getEclinicsData={getEclinicsData}
            labList={labList} ccList={ccList}
            shouldShowEclinicFilter={shouldShowEclinicFilter}
            shouldShowLabFilter={shouldShowLabFilter}
            shouldShowCcFilter={shouldShowCcFilter}
            onOrderStatusChange={handleOrderStatusChange}
            onSearch={handleSearch}
            onReset={handleReset}
            loading={loading}
          />
        </Box>

        <OrdersTable
          orders={orders} sort={sort} onSort={handleSort}
          selectedOrderIds={selectedOrderIds} isAllSelected={isAllSelected} isIndeterminate={isIndeterminate}
          onSelectAll={handleSelectAll} onSelectOrder={handleSelectOrder} onOrderIdClick={handleOrderIdClick}
          editingId={editingId} editValue={editValue} setEditValue={setEditValue}
          loading={loading} onSaveBookingId={handleSaveBookingId} onEditClick={handleEditClick} onCancelEdit={handleCancelEdit}
          onUploadClick={handleUploadClick} onStatusClick={handleStatusClick}
          bulkDownloadLoading={bulkDownloadLoading} onBulkDownload={handleBulkDownload}
        />

        <Popover
          open={Boolean(statusMenuAnchor)} anchorEl={statusMenuAnchor} onClose={handleStatusMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ p: 1 }}>
            <MenuItem onClick={() => openCancelDialog(selectedOrderForStatus)} sx={{ color: "error.main" }}>
              <CancelIcon sx={{ mr: 1 }} /> Cancel Order
            </MenuItem>
          </Box>
        </Popover>

        <Box sx={{ flexShrink: 0 }}>
          <PaginationSection
            currentPage={currentPage} totalPages={totalPages} totalElements={totalElements}
            pageSize={pageSize} onPageSizeChange={handlePageSizeChange}
            onPageChange={fetchOrdersWithParams} fetchOrdersWithParams={fetchOrdersWithParams}
          />
        </Box>

        <CancelOrderDialog
          open={cancelDialogOpen}
          onClose={closeCancelDialog}
          orderToCancel={orderToCancel}
          loading={loading}
          onConfirm={handleCancelOrder}
        />

        <UploadReportModal
          open={reportUploadModal}
          onClose={handleCloseModal}
          selectedOrderDetails={selectedOrderDetails}
          selectedOrder={selectedOrder}
          labTestsOrderReports={labTestsOrderReports}
          onViewClick={handleViewClick}
          onDownloadClick={handleDownloadClick}
          onDeleteClick={handleDeleteClick}
          onOpenUploadModal={() => setUploadReportsModalOpen(true)}
        />

        <Dialog open={genaratePDF} onClose={() => setGenaratePDF(false)} maxWidth="lg" fullWidth sx={{ zIndex: 999999 }}>
          <DialogContent>{genaratePDFData && <TRFpopup data={genaratePDFData} vialsData={vialsData} />}</DialogContent>
        </Dialog>

        <FileUploadModal
          open={uploadReportsModalOpen} onClose={() => setUploadReportsModalOpen(false)}
          selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles}
          reportStatus={reportStatus} setReportStatus={setReportStatus}
          selectedLabReport={selectedLabReport} setSelectedLabReport={setSelectedLabReport}
          typedLabReport={typedLabReport} setTypedLabReport={setTypedLabReport}
          patientLabTestsOrderDetails={patientLabTestsOrderDetails}
          handleSave={handleSaveUpload} loading={loading}
        />

        <Drawer
          anchor="right" open={sidebarOpen} onClose={handleCloseSidebar}
          sx={{
            "& .MuiDrawer-paper": {
              width: { xs: "100%", sm: 780, md: 840 },
              maxWidth: "100vw",
              boxSizing: "border-box",
              backgroundColor: "#f9fafc",
              zIndex: 20000,
            },
            zIndex: 20000,
          }}
        >
          <Box sx={{ px: 3, pt: 1.5, pb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, pb: 1, borderBottom: "1px solid #e5e7eb" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#1e293b", fontSize: "1rem" }}>
                Order ID: {selectedOrderDetails?.orderId || selectedOrder?.id || "—"}
              </Typography>
              <IconButton size="small" onClick={handleCloseSidebar} sx={{ color: "#64748b", p: 0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Tabs
              value={sidebarTab}
              onChange={(e, val) => setSidebarTab(val)}
              sx={{
                mb: 2.5,
                borderBottom: "1px solid #e2e8f0",
                "& .MuiTabs-indicator": { backgroundColor: "#6366f1", height: 3, borderRadius: "3px 3px 0 0" },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  minHeight: 42,
                  py: 1,
                  px: 2,
                  color: "#64748b",
                  "&.Mui-selected": { color: "#6366f1" },
                },
              }}
            >
              <Tab
                icon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Order Details"
              />
              <Tab
                icon={<MedicalServicesOutlinedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Clinical & Sample Action"
              />
            </Tabs>

            {sidebarTab === 0 && (
              <>
                <Accordion defaultExpanded sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold">Order Information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <OrderInfoFields
                        selectedOrderDetails={selectedOrderDetails}
                        getStatusLabelFromDetails={getStatusLabelFromDetails}
                      />
                      <EclinicReassignSection
                        selectedOrderDetails={selectedOrderDetails}
                        onReassign={handleReassignEclinic}
                        loading={loading}
                      />
                      <LabReassignSection
                        selectedOrderDetails={selectedOrderDetails}
                        labOptions={availableLabOptions}
                        loadingLabs={loadingReassignLabs}
                        onInitiateReassign={handleValidateLabReassign}
                        validating={validatingLab}
                        selectedLab={selectedLabToReassign}
                        setSelectedLab={setSelectedLabToReassign}
                      />
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="500">Lab Confirm ID:</Typography>
                          <SidebarLabConfirmId
                            selectedOrder={selectedOrder} editingId={editingId}
                            editValue={editValue} setEditValue={setEditValue}
                            loading={loading} onSave={handleSaveBookingId} onCancel={handleCancelEdit}
                            onStartEdit={(id, val) => { setEditingId(id); setEditValue(val); }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="500">Lab Confirm ID Status:</Typography>
                          <Typography variant="body2" fontWeight="bold" color="text.primary">
                            {getLabConfirmIdStatusDisplay(selectedOrderDetails)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="500">Total Amount (INR):</Typography>
                          <Typography variant="body2" fontWeight="bold" color="text.primary">
                            {selectedOrderDetails?.paidAmount ? Number(selectedOrderDetails.paidAmount).toLocaleString("en-IN") : "—"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="500">TRF:</Typography>
                          <TRFCell
                            consignmentId={selectedOrderDetails?.consignmentId}
                            onGenerateTRF={ongenarateTRF}
                            selectedOrderDetails={selectedOrderDetails}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="500" gutterBottom>Remarks:</Typography>
                          {editingRemarks && !isOrderCancelled(selectedOrderDetails?.orderStatus || selectedOrder?.orderStatus) ? (
                            <RemarksEditMode
                              remarksValue={remarksValue}
                              setRemarksValue={setRemarksValue}
                              loading={loading}
                              onCancel={handleCancelRemarksEdit}
                              onSave={handleSaveRemarks}
                            />
                          ) : (
                            <RemarksViewMode
                              remarks={selectedOrderDetails?.remarks}
                              onEdit={handleEditRemarksClick}
                              isCancelled={isOrderCancelled(selectedOrderDetails?.orderStatus || selectedOrder?.orderStatus)}
                            />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Typography variant="subtitle1" fontWeight="bold">Lab Tests (Modify / Cancel)</Typography>
                      {predLabTestCount !== null && (
                        <Chip
                          label={`${predLabTestCount} Active`}
                          size="small"
                          color={predLabTestCount > 0 ? "primary" : "default"}
                          sx={{ height: 20, fontSize: "11px", fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <OrderLabTestsSection
                      orderId={selectedOrderDetails?.orderId || selectedOrder?.id}
                      labPartner={selectedOrderDetails?.labPartner || selectedOrder?.labPartner || selectedOrderDetails?.partnerName}
                      showSnackbarMessage={showSnackbarMessage}
                      onCountChange={(count) => setPredLabTestCount(count)}
                      onTestDeleted={() => fetchOrders(currentPage)}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold">Order Tracking</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <OrderTrackingDetails orderTrackDetails={orderTrackDetails} testType={selectedOrderDetails?.testType} />
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold">Smart Reports</Typography>
                    {smartReports.length > 0 && <Chip label={`${smartReports.length} Available`} size="small" color="primary" sx={{ ml: 2 }} />}
                  </AccordionSummary>
                  <AccordionDetails>
                    {smartReports.length > 0 ? (
                      <SmartReportsTable
                        smartReports={smartReports}
                        onView={handleSmartReportView}
                        onDownload={handleSmartReportDownload}
                      />
                    ) : (
                      <SmartReportsEmpty />
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold">PACS / DICOM Studies</Typography>
                    {pacsStudies.length > 0 && (
                      <Chip label={`${pacsStudies.length} Available`} size="small" color="primary" sx={{ ml: 2 }} />
                    )}
                  </AccordionSummary>
                  <AccordionDetails>
                    {pacsLoading ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : pacsStudies.length > 0 ? (
                      <PacsStudiesTable
                        studies={pacsStudies}
                        onOpenViewer={handleOpenViewer}
                        onDownloadZip={handleDownloadPacsZip}
                        onViewDetails={(study) => setSelectedPacsStudy(study)}
                        onViewDicom={(study) => setDicomViewerStudy(study)}
                      />
                    ) : (
                      <EmptyState message="No PACS / DICOM studies available for this order" />
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold">Report Details</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {labTestsOrderReports.length > 0 ? (
                      <ReportTable reports={labTestsOrderReports} onView={handleViewClick} onDownload={handleDownloadClick} onDelete={handleDeleteClick} />
                    ) : isMobilabOrder ? (
                      <Box sx={{ textAlign: "center", py: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">No reports available</Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={fetchingMobilabReport ? <CircularProgress size={16} /> : <RefreshOutlinedIcon />}
                          onClick={handleFetchMobilabReport}
                          disabled={fetchingMobilabReport || isOrderCancelled(selectedOrderDetails?.orderStatus || selectedOrder?.orderStatus)}
                          sx={{
                            textTransform: "none",
                            borderRadius: "20px",
                            borderColor: "#004c97",
                            color: "#004c97",
                            fontWeight: 600,
                            "&:hover": { backgroundColor: "rgba(0,76,151,0.04)", borderColor: "#002f5f" },
                          }}
                        >
                          {fetchingMobilabReport ? "Fetching Report..." : "Fetch Report"}
                        </Button>
                      </Box>
                    ) : (
                      <EmptyState message="No reports available" />
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold">Report Logs</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {reportsLog.length > 0 ? (
                      <ReportLogsTable reportsLog={reportsLog} />
                    ) : (
                      <EmptyState message="No reports available" />
                    )}
                  </AccordionDetails>
                </Accordion>
              </>
            )}

            {sidebarTab === 1 && (
              <OrderClinicalAndSampleTrack
                order={selectedOrder}
                orderDetails={selectedOrderDetails}
                showSnackbarMessage={showSnackbarMessage}
              />
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3, pt: 2, borderTop: "1px solid #e5e7eb" }}>
              <Button variant="outlined" onClick={handleCloseSidebar}>Close</Button>
            </Box>
          </Box>
        </Drawer>

        <PacsStudyDetailDialog
          open={!!selectedPacsStudy}
          onClose={() => setSelectedPacsStudy(null)}
          study={selectedPacsStudy}
          onOpenViewer={handleOpenViewer}
          onDownloadZip={handleDownloadPacsZip}
          onViewDicom={(study) => setDicomViewerStudy(study)}
        />

        <DicomViewerDialog
          open={!!dicomViewerStudy}
          onClose={() => setDicomViewerStudy(null)}
          study={dicomViewerStudy}
          onDownloadZip={handleDownloadPacsZip}
        />

        <ConfirmLabReassignDialog
          open={confirmLabDialog.open}
          onClose={handleCloseConfirmLabDialog}
          confirmData={confirmLabDialog}
          onConfirm={handleConfirmLabReassign}
          loading={reassigningLab}
          errorMessage={confirmLabError}
          onClearError={() => setConfirmLabError(null)}
          onReasonChange={(reason) =>
            setConfirmLabDialog((prev) => ({ ...prev, reason }))
          }
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{
            zIndex: 99999,
            maxWidth: sidebarOpen
              ? { xs: "calc(100vw - 32px)", sm: "720px", md: "800px" }
              : { xs: "calc(100vw - 32px)", sm: "520px" },
            width: "auto",
            top: { xs: 16, sm: 20 },
            right: { xs: 16, sm: 24 },
          }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            sx={{
              zIndex: 99999,
              width: "100%",
              maxWidth: "100%",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              whiteSpace: "normal",
              boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
              "& .MuiAlert-message": {
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                whiteSpace: "normal",
                width: "100%",
              },
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}